import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import { useToast } from '@/components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useCheckout } from '@/context/CheckoutContext';
import { useNotifications } from '@/context/NotificationContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { useHeaderOffset } from '@/hooks/useHeaderOffset';
import StandardPage from '@/components/StandardPage';
import NearbyStoresSelector, { type NearbyStore } from '@/components/NearbyStoresSelector';
import { QuantitySelector } from '@/components/QuantitySelector';
import NoteCard from '@/components/NoteCard';
import { ChatButton } from '@/components/chat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ShoppingCart, Heart, Users, MapPin, MessageSquareText, X, ArrowRight } from '@/components/icons';
import {
  Apple,
  Candy,
  ChevronDown,
  CupSoda,
  Milk,
  Package,
  Pizza,
  Play,
  Shirt,
  ShoppingBag,
  ShoppingBasket as LucideShoppingCart,
  Snowflake,
  Sparkles,
  type LucideIcon,
  UtensilsCrossed,
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { getProductById } from '@/lib/supabase/products';
import { getProfileById, getProfilesByIds, type UserProfile } from '@/lib/supabase/profiles';
import { getUserNotesByUserId } from '@/lib/supabase';
import { getStoreByIdWithMembers, getStoreBySlug } from '@/lib/supabase/stores';
import { resolveStoreOfficeChatRecipientIds } from '@/lib/supabase/storeOfficeChatContacts';
import { getStoreAddresses } from '@/lib/supabase/storeAddresses';
import { StoreGetDirectionsSidebar } from '@/components/store/StoreGetDirectionsSidebar';
import { formatAddressForDisplay, formatCurrency } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { addCartItem, updateCartItem, removeCartItem, getCartForUser, getOpenCartsForUser } from '@/lib/supabase/carts';
import { getCartsSharedWithMe } from '@/lib/supabase/sharedCarts';
import { addToTempCart, updateTempCartItemQuantity, removeFromTempCart, getTempCartAsItems, clearTempCart } from '@/lib/tempCart';
import { addGroupMember, createGroupChat, getUserGroupChats, setGroupChatStarter } from '@/lib/supabase/chat';
import { getOrCreateAnonymousSupportSessionId } from '@/lib/anonymousSupportChat';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { usePlayFavoriteSound } from '@/hooks/usePlayFavoriteSound';
import { useVibrate } from '@/hooks/useVibrate';
import { useFavorites } from '@/hooks/useFavorites';
import { useSyncLocalCart } from '@/hooks/useSyncLocalCart';
import { useFavoritesSidebar } from '@/context/FavoritesSidebarContext';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { useChatContext } from '@/context/ChatContext';
import FayeStoreSplash from '@/components/FayeStoreSplash';
import {
  getActiveCartPreference,
  getActiveCollaborativeMetaForStore,
  setActiveSharedCartExplicitByStore,
  setActiveSharedCartThemePriority,
} from '@/lib/activeCartPreference';
import { useDebouncedCallback } from 'use-debounce';
import VerifiedBadge from '@/components/VerifiedBadge';

const DEFAULT_MINIBID_STORE: NearbyStore = {
  id: '30e79df7-81e8-4c5f-a825-602e5899d961',
  name: 'Minibid 24hs',
  slug: 'minibid',
  avatar_url: null,
  latitude: 0,
  longitude: 0,
};

const MINIBID_LAST_STORE_STORAGE_KEY = 'minibid24hs:last-open-store-id';

const getInitialMinibidStore = (): NearbyStore => {
  if (typeof window === 'undefined') {
    return DEFAULT_MINIBID_STORE;
  }

  const params = new URLSearchParams(window.location.search);
  const queryStoreId = params.get('storeId')?.trim();
  const queryStoreSlug = params.get('storeSlug')?.trim();
  let fallbackStoreId: string | null = null;

  try {
    fallbackStoreId = localStorage.getItem(MINIBID_LAST_STORE_STORAGE_KEY)?.trim() || null;
  } catch {
    fallbackStoreId = null;
  }

  const initialStoreId = queryStoreId || fallbackStoreId;
  if (!initialStoreId || initialStoreId === DEFAULT_MINIBID_STORE.id) {
    return DEFAULT_MINIBID_STORE;
  }

  return {
    ...DEFAULT_MINIBID_STORE,
    id: initialStoreId,
    name: 'Tienda',
    slug: queryStoreSlug || '',
  };
};

// Category and subcategory mappings
const PRODUCT_CATEGORIES = [
  { value: 'groceries', label: 'Almacén' },
  { value: 'electronics', label: 'Electrónica' },
  { value: 'clothing', label: 'Ropa' },
  { value: 'book', label: 'Libros' },
  { value: 'vehicle', label: 'Vehículos' },
  { value: 'art', label: 'Arte' },
  { value: 'diversion', label: 'Diversión' },
  { value: 'propiedades', label: 'Propiedades' },
  { value: 'tours', label: 'Excursiones' },
  { value: 'clases_particulares', label: 'Clases' },
];

const GROCERIES_SUBCATEGORIES = [
  { value: 'bebidas', label: 'Bebidas' },
  { value: 'bebidas_con_alcohol', label: 'Bebidas c/ alcohol' },
  { value: 'comestibles', label: 'Comestibles' },
  { value: 'golosinas', label: 'Golosinas' },
  { value: 'lacteos', label: 'Lácteos' },
  { value: 'congelados', label: 'Congelados' },
  { value: 'panaderia', label: 'Panadería' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'cuidado_personal', label: 'Cuidado personal' },
  { value: 'mascotas', label: 'Mascotas' },
  { value: 'otros', label: 'Otros' },
];

const KEYWORD_STOPWORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'con', 'sin', 'para', 'por', 'una', 'uno', 'unos', 'unas',
  'y', 'o', 'a', 'al', 'en', 'un', 'kg', 'gr', 'ml', 'lt', 'pack', 'x', 'the', 'and', 'off'
]);

const KEYWORD_ICONS: Array<[LucideIcon, LucideIcon]> = [
  [LucideShoppingCart, ShoppingBag],
  [Apple, Sparkles],
  [UtensilsCrossed, Candy],
  [CupSoda, Sparkles],
  [Milk, Package],
  [Candy, Sparkles],
  [Shirt, Sparkles],
  [UtensilsCrossed, Pizza],
  [Package, Snowflake],
];

const AISLE_COLOR = 'bg-primary/10 dark:bg-primary/10 border-primary/25 dark:border-primary/35';
const KEYWORD_AISLE_COLOR = AISLE_COLOR;
const TEAM_ACTIVITY_PAGE_SIZE = 3;
const CATALOG_INITIAL_RENDER_PER_AISLE = 8;
const CATALOG_RENDER_BATCH = 8;
const FIRST_VIEW_TOP_GAP_PX = 8;
const MOBILE_TOP_COMPENSATION_DEADZONE_PX = 4;
const MOBILE_FIXED_BOTTOM_PADDING_CSS = 'calc(env(safe-area-inset-bottom, 0px) + 7rem)';

const isSharedCartNotFoundOrExpired = (error: unknown): boolean => {
  const message = String((error as any)?.message || error || '').toLowerCase();
  const code = String((error as any)?.code || '').toLowerCase();
  return message.includes('shared cart not found or expired')
    || message.includes('canasto compartido invalido')
    || message.includes('canasto compartido inválido')
    || message.includes('carrito compartido invalido')
    || message.includes('carrito compartido inválido')
    || message.includes('shared cart not found')
    || message.includes('relation "public.shared_carts" does not exist')
    || message.includes('relation "shared_carts" does not exist')
    || code === '42p01';
};

interface KeywordAisleGroup {
  aisle: {
    id: string;
    name: string;
    icon1: LucideIcon;
    icon2: LucideIcon;
    color: string;
  };
  products: Product[];
}

const normalizeToken = (token: string): string => token
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

const extractProductKeywords = (text: string): string[] => text
  .split(/[^\p{L}\p{N}]+/u)
  .map(normalizeToken)
  .filter((token) => token.length >= 3 && !KEYWORD_STOPWORDS.has(token));

const humanizeKeyword = (keyword: string): string => keyword.charAt(0).toUpperCase() + keyword.slice(1);

// Get label for category
const getCategoryLabel = (categoryValue: string): string => {
  const category = PRODUCT_CATEGORIES.find(c => c.value === categoryValue);
  return category?.label || keyword.charAt(0).toUpperCase() + categoryValue.slice(1);
};

// Get label for groceries subcategory
const getSubcategoryLabel = (subcategoryValue: string): string => {
  const subcategory = GROCERIES_SUBCATEGORIES.find(s => s.value === subcategoryValue);
  return subcategory?.label || subcategoryValue.charAt(0).toUpperCase() + subcategoryValue.slice(1);
};

// Get icon pair for category/subcategory
const getAisleIcon = (aisleId: string, index: number): [LucideIcon, LucideIcon] => {
  return KEYWORD_ICONS[index % KEYWORD_ICONS.length];
};

// Group products by category and subcategory instead of keywords
const groupProductsByCategoryAisles = (products: Product[]): KeywordAisleGroup[] => {
  // Separate auctions from regular products
  const auctionProducts = products.filter((p: any) => p.is_auction === true);
  const regularProducts = products.filter((p: any) => p.is_auction !== true);

  const groups: KeywordAisleGroup[] = [];
  let iconIndex = 0;

  // Add auctions aisle first if there are any
  if (auctionProducts.length > 0) {
    const [icon1, icon2] = getAisleIcon('auctions', iconIndex);
    groups.push({
      aisle: {
        id: 'auctions',
        name: 'Subastas',
        icon1,
        icon2,
        color: AISLE_COLOR,
      },
      products: auctionProducts.sort((a, b) => a.title.localeCompare(b.title)),
    });
    iconIndex++;
  }

  // Group regular products by category
  const categoryGroups: Record<string, Product[]> = {};
  const subcategoryGroups: Record<string, Product[]> = {};

  for (const product of regularProducts) {
    const category = (product as any).product_type || 'groceries';
    
    if (category === 'groceries') {
      // For groceries, group by subcategory
      const subcategory = (product as any).groceries_subcategory || 'otros';
      const key = `groceries-${subcategory}`;
      if (!subcategoryGroups[key]) subcategoryGroups[key] = [];
      subcategoryGroups[key].push(product);
    } else {
      // For other categories, group by category
      if (!categoryGroups[category]) categoryGroups[category] = [];
      categoryGroups[category].push(product);
    }
  }

  // Add category aisles
  PRODUCT_CATEGORIES.forEach((category) => {
    const categoryValue = category.value;
    
    if (categoryValue === 'groceries') {
      // Add subcategory aisles for groceries
      GROCERIES_SUBCATEGORIES.forEach((subcategory) => {
        const key = `groceries-${subcategory.value}`;
        const productsInSubcategory = subcategoryGroups[key] || [];
        
        if (productsInSubcategory.length > 0) {
          const [icon1, icon2] = getAisleIcon(key, iconIndex);
          groups.push({
            aisle: {
              id: key,
              name: subcategory.label,
              icon1,
              icon2,
              color: AISLE_COLOR,
            },
            products: productsInSubcategory.sort((a, b) => a.title.localeCompare(b.title)),
          });
          iconIndex++;
        }
      });
    } else {
      // Add category aisles for non-groceries
      const productsInCategory = categoryGroups[categoryValue] || [];
      
      if (productsInCategory.length > 0) {
        const [icon1, icon2] = getAisleIcon(categoryValue, iconIndex);
        groups.push({
          aisle: {
            id: categoryValue,
            name: category.label,
            icon1,
            icon2,
            color: AISLE_COLOR,
          },
          products: productsInCategory.sort((a, b) => a.title.localeCompare(b.title)),
        });
        iconIndex++;
      }
    }
  });

  // Fallback: if no products in any aisle, show all
  if (groups.length === 0) {
    return [{
      aisle: {
        id: 'all-products',
        name: 'Productos',
        icon1: LucideShoppingCart,
        icon2: ShoppingBag,
        color: AISLE_COLOR,
      },
      products: [...products].sort((a, b) => a.title.localeCompare(b.title)),
    }];
  }

  return groups;
};

const groupProductsByKeywordAisles = groupProductsByCategoryAisles;

// Función para generar número pseudo-aleatorio seeded
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Función para obtener icono aleatorio de forma consistente usando seed
const getRandomAisleIcon = (aisleId: string, index: number, icon1: LucideIcon, icon2: LucideIcon): LucideIcon => {
  const seed = aisleId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) + index;
  const rand = seededRandom(seed);
  return rand < 0.5 ? icon1 : icon2;
};

// Función para limpiar HTML de descripciones
const stripHtml = (html: string): string => {
  if (!html) return '';
  try {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  } catch (err) {
    console.warn('Error limpiando HTML:', err);
    return html;
  }
};

interface Product {
  id: string;
  store_id?: string;
  title: string;
  description: string;
  fixed_sale_price: number;
  fixed_rental_price?: number | null;
  stock: number;
  main_image_url?: string | null;
  cover_url?: string | null;
  image_urls?: string[];
  details?: Record<string, any> | null;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  details?: Record<string, any> | null;
}

type SharedCartActivityLog = {
  id: string;
  shared_cart_id: string;
  actor_user_id: string;
  action: 'added' | 'updated' | 'removed' | 'cleared';
  product_id?: string | null;
  product_title?: string | null;
  quantity_before?: number | null;
  quantity_after?: number | null;
  quantity_delta?: number | null;
  created_at: string;
};

interface StoreDetails {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  avatar_url?: string | null;
  status?: string | null;
  is_public?: boolean | null;
  location_display_name?: string | null;
  owner_id?: string | null;
  schedule?: any;
  timezone?: string | null;
  schedule_timezone?: string | null;
  delivery_available?: boolean | null;
  pickup_available?: boolean | null;
  delivery_enabled?: boolean | null;
  pickup_enabled?: boolean | null;
  supports_delivery?: boolean | null;
  supports_pickup?: boolean | null;
  accepts_delivery?: boolean | null;
  accepts_pickup?: boolean | null;
}

interface StoreTeamMember {
  user_id: string;
  role: string;
  display_name: string;
  username?: string | null;
  avatar_url?: string | null;
  verification_status?: 'pending' | 'submitted' | 'verified' | 'rejected' | 'expired';
}

const STORE_ROLE_LABELS: Record<string, string> = {
  owner: 'Dueño',
  admin: 'Admin',
  staff: 'Equipo',
  member: 'Miembro',
};

const getStoreRoleLabel = (role: string): string => STORE_ROLE_LABELS[role] || 'Equipo';
const getStoreGroupDescription = (storeId: string): string => `store-group:${storeId}`;

const getStoreWeekAndTimezone = (storeObj: any): { week: any[]; tz: string } => {
  const s = storeObj?.schedule;
  let week: any[] = [];
  let tz = null;
  if (Array.isArray(s)) {
    week = s;
    tz = storeObj?.timezone || storeObj?.schedule_timezone || null;
  } else if (s && typeof s === 'object' && Array.isArray(s.week)) {
    week = s.week;
    tz = s.timezone || storeObj?.timezone || storeObj?.schedule_timezone || null;
  }
  return {
    week,
    tz: tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
};

const getStoreNowParts = (tz: string) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const wk = parts.find((p) => p.type === 'weekday')?.value || 'Mon';
  const hourStr = parts.find((p) => p.type === 'hour')?.value || '00';
  const minuteStr = parts.find((p) => p.type === 'minute')?.value || '00';
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return {
    wk,
    dayIdx: map[wk] ?? 0,
    currentMin: (isNaN(hour) ? 0 : hour) * 60 + (isNaN(minute) ? 0 : minute),
  };
};

const isStoreOpenNow = (storeObj: any): boolean => {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return false;
    const { dayIdx, currentMin } = getStoreNowParts(tz);
    const periods = week[dayIdx] || [];

    for (const p of periods) {
      if (!p || !p.start || !p.end) continue;
      const mStart = p.start.match(/^(\d{2}):(\d{2})$/);
      const mEnd = p.end.match(/^(\d{2}):(\d{2})$/);
      if (!mStart || !mEnd) continue;
      const sMin = parseInt(mStart[1], 10) * 60 + parseInt(mStart[2], 10);
      const eMin = parseInt(mEnd[1], 10) * 60 + parseInt(mEnd[2], 10);
      if (sMin <= currentMin && currentMin < eMin) return true;
    }
    return false;
  } catch {
    return false;
  }
};

const getTodayClosingTime = (storeObj: any): string | null => {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return null;
    const { dayIdx, currentMin } = getStoreNowParts(tz);
    const periods = week[dayIdx] || [];

    for (const p of periods) {
      if (!p || !p.start || !p.end) continue;
      const mStart = p.start.match(/^(\d{2}):(\d{2})$/);
      const mEnd = p.end.match(/^(\d{2}):(\d{2})$/);
      if (!mStart || !mEnd) continue;
      const sMin = parseInt(mStart[1], 10) * 60 + parseInt(mStart[2], 10);
      const eMin = parseInt(mEnd[1], 10) * 60 + parseInt(mEnd[2], 10);
      if (sMin <= currentMin && currentMin < eMin) return p.end;
    }
    return null;
  } catch {
    return null;
  }
};

const getNextOpeningTime = (storeObj: any): { time: string; dayIdx: number } | null => {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return null;
    const { dayIdx: todayIdx, currentMin } = getStoreNowParts(tz);

    const todayPeriods = week[todayIdx] || [];
    for (const p of todayPeriods) {
      if (!p || !p.start) continue;
      const mStart = p.start.match(/^(\d{2}):(\d{2})$/);
      if (!mStart) continue;
      const sMin = parseInt(mStart[1], 10) * 60 + parseInt(mStart[2], 10);
      if (sMin > currentMin) return { time: p.start, dayIdx: todayIdx };
    }

    for (let d = 1; d <= 7; d++) {
      const idx = (todayIdx + d) % 7;
      const periods = week[idx] || [];
      for (const p of periods) {
        if (!p || !p.start) continue;
        const mStart = p.start.match(/^(\d{2}):(\d{2})$/);
        if (!mStart) continue;
        return { time: p.start, dayIdx: idx };
      }
    }
    return null;
  } catch {
    return null;
  }
};

export default function Minibid24hsPage() {
  const location = useLocation();
  const { session } = useSession();
  const { toast } = useToast();
  const { openSidebar: openCheckoutSidebar, sidebarOpen: checkoutSidebarOpen, setPreferredStoreId, setPreferredStoreName } = useCheckout();
  useNotifications();
  useUserMenu();
  const playBeep = usePlayBeep();
  const playFavoriteSound = usePlayFavoriteSound();
  const vibrate = useVibrate();
  const { headerHeight, isDesktop } = useHeaderOffset();
  const { toggleFavorite, isFavorited, favorites } = useFavorites();
  const { isOpen: favoritesSidebarOpen, open: openFavoritesSidebar, close: closeFavoritesSidebar } = useFavoritesSidebar();
  const { setProductSidebarOpen, setSelectedProduct, productSidebarOpen, startProductPanning, pauseProductPanning, productPanningActive } = useContentSidebar();
  const { openChatWindow } = useChatContext();
  const [selectedStore, setSelectedStore] = useState<NearbyStore>(() => getInitialMinibidStore());
  const [promoCardActive, setPromoCardActive] = useState(false);
  const [selectedAisle, setSelectedAisle] = useState<string | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [storeDetails, setStoreDetails] = useState<StoreDetails | null>(null);
  const [storeTeam, setStoreTeam] = useState<StoreTeamMember[]>([]);
  const [storeAddresses, setStoreAddresses] = useState<any[]>([]);
  const [storeChatMemberIds, setStoreChatMemberIds] = useState<string[]>([]);
  const [storeChatMembersLoading, setStoreChatMembersLoading] = useState(false);
  const [directionsSidebarOpen, setDirectionsSidebarOpen] = useState(false);
  const [storeMetaLoading, setStoreMetaLoading] = useState(false);
  const [teamProfileSidebarOpen, setTeamProfileSidebarOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<StoreTeamMember | null>(null);
  const [selectedTeamProfile, setSelectedTeamProfile] = useState<UserProfile | null>(null);
  const [selectedTeamProfileLoading, setSelectedTeamProfileLoading] = useState(false);
  const [selectedTeamNotes, setSelectedTeamNotes] = useState<any[]>([]);
  const [selectedTeamNotesLoading, setSelectedTeamNotesLoading] = useState(false);
  const [selectedTeamNotesVisibleCount, setSelectedTeamNotesVisibleCount] = useState(TEAM_ACTIVITY_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [rentStoreLoading, setRentStoreLoading] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartDataStoreId, setCartDataStoreId] = useState<string | null>(null);
  const [sharedActivityLogs, setSharedActivityLogs] = useState<SharedCartActivityLog[]>([]);
  const [sharedActivityActorNameByUserId, setSharedActivityActorNameByUserId] = useState<Record<string, string>>({});
  const [fayeSplashStoreName, setFayeSplashStoreName] = useState<string | null>(null);
  const [fayeSplashAisleName, setFayeSplashAisleName] = useState<string | null>(null);
  const pendingFayeAisleProductsRef = useRef<Product[] | null>(null);
  const [cartSyncing, setCartSyncing] = useState(false);
  const [startingStoreChat, setStartingStoreChat] = useState(false);
  const [updatingProductIds, setUpdatingProductIds] = useState<Set<string>>(new Set());
  const [viewportWidth, setViewportWidth] = useState(0);
  const prevCartItemsLength = useRef(cartItems.length);
  const autoSyncAttemptedForUserRef = useRef<string | null>(null);
  const nearbySelectorRef = useRef<HTMLDivElement>(null);
  const catalogSectionRef = useRef<HTMLDivElement>(null);
  const [mobileTopCompensation, setMobileTopCompensation] = useState(0);
  const navbarVisibleBottomRef = useRef(0);
  const firstScrollSnapDoneRef = useRef(false);
  const touchStartYRef = useRef<number | null>(null);
  const storeHeaderRef = useRef<HTMLDivElement>(null);
  const activeStoreIdRef = useRef(selectedStore.id);
  const previousSelectedStoreIdRef = useRef(selectedStore.id);
  const processedStoreReselectNonceRef = useRef(0);
  const selectorDrivenAutoScrollRef = useRef(false);
  const previousCtaStoreIdRef = useRef(selectedStore.id);
  const storeCtaVisibleRef = useRef(false);
  const storeCartSummaryRef = useRef<{ units: number; total: number }>({ units: 0, total: 0 });
  const [storeViewMode, setStoreViewMode] = useState<'carousel' | 'directory'>('carousel');
  const [storesCarouselLoading, setStoresCarouselLoading] = useState(true);
  const [catalogInView, setCatalogInView] = useState(false);
  const [storeReselectNonce, setStoreReselectNonce] = useState(0);
  const [visibleAisleProductCounts, setVisibleAisleProductCounts] = useState<Record<string, number>>({});
  const mobilePageBottomPadding = !isDesktop
    ? MOBILE_FIXED_BOTTOM_PADDING_CSS
    : '2.5rem';
  const [initialFeedStoreId, setInitialFeedStoreId] = useState<string | null>(null);
  const [firstStoreHeaderPassed, setFirstStoreHeaderPassed] = useState(false);
  const firstStoreHeaderPassedRef = useRef(false);
  const selectorVisibleRef = useRef(false);
  const { syncLocalCartToDB } = useSyncLocalCart();
  const isFirstFeedStore = initialFeedStoreId
    ? selectedStore.id === initialFeedStoreId
    : true;

  const emitStoreCartCTA = useCallback((
    visible: boolean,
    units = 0,
    total = 0,
    storeName = '',
    storeId = '',
    options?: { isShared?: boolean }
  ) => {
    window.dispatchEvent(new CustomEvent('minibid-store-cart-cta', {
      detail: {
        visible,
        units,
        total,
        storeName,
        storeId,
        isShared: !!options?.isShared,
      },
    }));
  }, []);

  const getActiveSharedMetaForStore = useCallback((storeId: string) => {
    const collaborative = getActiveCollaborativeMetaForStore(storeId, { search: window.location.search });
    return {
      isShared: collaborative.isActive,
      activeCartId: collaborative.activeCartId,
    };
  }, []);

  const getValidatedStoreCartSummary = useCallback(() => {
    const rawUnits = storeCartSummaryRef.current.units;
    const rawTotal = storeCartSummaryRef.current.total;

    const units = Number.isFinite(rawUnits) ? Math.max(0, Math.trunc(rawUnits)) : 0;
    const total = Number.isFinite(rawTotal) ? Math.max(0, rawTotal) : 0;
    const isValid = Number.isFinite(rawUnits) && Number.isFinite(rawTotal);

    return { isValid, units, total };
  }, []);

  useEffect(() => {
    const validItems = cartItems.filter((item: any) => Number(item.quantity || 0) > 0);
    const units = validItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const total = validItems.reduce((sum, item) => sum + (Number(item.unit_price || 0) * Number(item.quantity || 0)), 0);
    storeCartSummaryRef.current = { units, total };
  }, [cartItems]);

  const hideStoreCartCTA = useCallback(() => {
    if (!storeCtaVisibleRef.current) {
      return;
    }

    storeCtaVisibleRef.current = false;
    window.dispatchEvent(new CustomEvent('minibid-store-cart-cta', {
      detail: { visible: false, units: 0, total: 0 },
    }));
  }, []);

  const scheduleStoreCartCTA = useCallback(() => {
    if (cartDataStoreId !== selectedStore.id) {
      hideStoreCartCTA();
      return;
    }

    if (cartLoading) {
      return;
    }

    if (isFirstFeedStore && storeViewMode !== 'directory' && !firstStoreHeaderPassedRef.current) {
      hideStoreCartCTA();
      return;
    }

    // Hide if the store selector is scrolled back into view
    if (isDesktop && selectorVisibleRef.current) {
      hideStoreCartCTA();
      return;
    }

    const summary = getValidatedStoreCartSummary();
    if (!summary.isValid) {
      // Show button with 0 items and $0 instead of hiding
      storeCtaVisibleRef.current = true;
      const activeMeta = getActiveSharedMetaForStore(selectedStore.id || '');
      emitStoreCartCTA(true, 0, 0, selectedStore.name || '', selectedStore.id || '', activeMeta);
      return;
    }

    storeCtaVisibleRef.current = true;
    const activeMeta = getActiveSharedMetaForStore(selectedStore.id || '');
    emitStoreCartCTA(true, summary.units, summary.total, selectedStore.name || '', selectedStore.id || '', activeMeta);
  }, [cartDataStoreId, cartLoading, emitStoreCartCTA, getActiveSharedMetaForStore, getValidatedStoreCartSummary, hideStoreCartCTA, isDesktop, isFirstFeedStore, selectedStore.id, selectedStore.name, storeViewMode]);

  const getStableHeaderOffset = useCallback(() => {
    const appHeader = document.querySelector('header.app-header') as HTMLElement | null;
    if (!appHeader) return 0;

    const hiddenAttr = appHeader.getAttribute('data-header-hidden');
    if (hiddenAttr === 'true') return 0;

    const headerRect = appHeader.getBoundingClientRect();
    const clampedHeight = Math.max(headerRect.height, 0);
    const visibleAttr = appHeader.getAttribute('data-header-visible');

    if (visibleAttr === 'true') {
      return clampedHeight;
    }

    return Math.max(Math.min(headerRect.bottom, clampedHeight), 0);
  }, []);

  const applyMobileTopCompensation = useCallback((delta: number) => {
    if (!Number.isFinite(delta)) return;

    const normalizedDelta = Math.round(delta);
    if (Math.abs(normalizedDelta) <= MOBILE_TOP_COMPENSATION_DEADZONE_PX) {
      return;
    }

    setMobileTopCompensation((prev) => {
      const absDelta = Math.abs(normalizedDelta);
      const maxStep = absDelta <= 12 ? 12 : absDelta <= 36 ? 20 : 30;
      const step = Math.max(-maxStep, Math.min(maxStep, normalizedDelta));
      const nextCompensation = Math.max(-96, Math.min(96, prev + step));
      return nextCompensation !== prev ? nextCompensation : prev;
    });
  }, []);

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobileTopCompensation(0);
      return;
    }

    let rafId = 0;
    let recalculationScheduled = false;
    const timeoutIds: number[] = [];

    const recalculateTopCompensation = () => {
      const selectorElement = nearbySelectorRef.current;
      if (!selectorElement) return;

      const headerBottom = getStableHeaderOffset();
      const selectorTop = selectorElement.getBoundingClientRect().top;
      const desiredTop = headerBottom + FIRST_VIEW_TOP_GAP_PX;
      const delta = Math.round(desiredTop - selectorTop);

      applyMobileTopCompensation(delta);
    };

    const scheduleRecalculation = () => {
      if (recalculationScheduled) return;
      recalculationScheduled = true;
      rafId = window.requestAnimationFrame(() => {
        recalculationScheduled = false;
        recalculateTopCompensation();
      });
    };

    const scheduleRecalculationBurst = () => {
      scheduleRecalculation();
      [80, 180, 320].forEach((delay) => {
        timeoutIds.push(window.setTimeout(scheduleRecalculation, delay));
      });
    };

    let headerResizeObserver: ResizeObserver | null = null;
    let selectorResizeObserver: ResizeObserver | null = null;

    scheduleRecalculationBurst();

    const appHeader = document.querySelector('header.app-header') as HTMLElement | null;
    const selectorElement = nearbySelectorRef.current;

    if (appHeader && typeof ResizeObserver !== 'undefined') {
      headerResizeObserver = new ResizeObserver(() => {
        scheduleRecalculation();
      });
      headerResizeObserver.observe(appHeader);
    }

    if (selectorElement && typeof ResizeObserver !== 'undefined') {
      selectorResizeObserver = new ResizeObserver(() => {
        scheduleRecalculation();
      });
      selectorResizeObserver.observe(selectorElement);
    }

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', scheduleRecalculation);
    window.addEventListener('orientationchange', scheduleRecalculation);
    if (visualViewport) {
      visualViewport.addEventListener('resize', scheduleRecalculation);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('resize', scheduleRecalculation);
      window.removeEventListener('orientationchange', scheduleRecalculation);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', scheduleRecalculation);
      }
      headerResizeObserver?.disconnect();
      selectorResizeObserver?.disconnect();
    };
  }, [applyMobileTopCompensation, getStableHeaderOffset, headerHeight, isDesktop, session?.user?.id]);

  useLayoutEffect(() => {
    if (isDesktop) return;

    const selectorElement = nearbySelectorRef.current;
    if (!selectorElement) return;

    const headerBottom = getStableHeaderOffset();
    navbarVisibleBottomRef.current = headerBottom;

    const selectorTop = selectorElement.getBoundingClientRect().top;
    const desiredTop = headerBottom + FIRST_VIEW_TOP_GAP_PX;
    const delta = Math.round(desiredTop - selectorTop);

    applyMobileTopCompensation(delta);
  }, [applyMobileTopCompensation, getStableHeaderOffset, headerHeight, isDesktop, selectedStore.id, storesCarouselLoading]);

  useEffect(() => {
    // Preserve fully native first scroll on all devices.
    firstScrollSnapDoneRef.current = true;
    touchStartYRef.current = null;
  }, []);

  useEffect(() => {
    const storeChanged = previousSelectedStoreIdRef.current !== selectedStore.id;
    const manualReselectTriggered = storeReselectNonce !== processedStoreReselectNonceRef.current;
    const selectorDrivenAutoScroll = selectorDrivenAutoScrollRef.current;
    selectorDrivenAutoScrollRef.current = false;

    if (!storeChanged && !manualReselectTriggered) {
      return;
    }

    previousSelectedStoreIdRef.current = selectedStore.id;
    processedStoreReselectNonceRef.current = storeReselectNonce;

    let frameId = 0;
    let frameId2 = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scrollToStoreHeader = () => {
      const headerElement = storeHeaderRef.current;
      if (!headerElement) return;

      if (!isDesktop && selectorDrivenAutoScroll) {
        window.dispatchEvent(new CustomEvent('app-header-visibility', { detail: { hidden: true } }));

        frameId = window.requestAnimationFrame(() => {
          frameId2 = window.requestAnimationFrame(() => {
            const currentHeaderElement = storeHeaderRef.current;
            if (!currentHeaderElement) return;

            const targetTop = currentHeaderElement.getBoundingClientRect().top + window.scrollY;
            const top = Math.max(0, targetTop - 8);
            window.scrollTo({ top, behavior: 'smooth' });
          });
        });
        return;
      }

      const visibleHeaderOffset = getStableHeaderOffset();
      const targetTop = headerElement.getBoundingClientRect().top + window.scrollY;
      // On desktop the sticky navbar hides while scrolling down, so don't reserve its current height.
      const finalOffset = isDesktop ? 12 : Math.max(visibleHeaderOffset + 12, 12);
      const top = targetTop - finalOffset;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    };

    frameId = window.requestAnimationFrame(() => {
      timeoutId = setTimeout(scrollToStoreHeader, 40);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(frameId2);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [getStableHeaderOffset, isDesktop, selectedStore.id, storeReselectNonce]);

  useEffect(() => {
    if (previousCtaStoreIdRef.current !== selectedStore.id) {
      previousCtaStoreIdRef.current = selectedStore.id;
      hideStoreCartCTA();
    }
  }, [hideStoreCartCTA, selectedStore.id]);

  useEffect(() => {
    activeStoreIdRef.current = selectedStore.id;
    setCartDataStoreId(null);
  }, [selectedStore.id]);

  // Sincronización inmediata de estado cuando cambia la tienda
  useLayoutEffect(() => {
    setSelectedAisle(null);
    setStoreDetails(null);
    setStoreTeam([]);
    setStoreAddresses([]);
    setSelectedTeamMember(null);
    setSelectedTeamProfile(null);
    setSelectedTeamNotes([]);
    setSelectedTeamNotesVisibleCount(TEAM_ACTIVITY_PAGE_SIZE);
    setVisibleAisleProductCounts({});
    setFirstStoreHeaderPassed(false);
    firstStoreHeaderPassedRef.current = false;
  }, [selectedStore.id]);
  useEffect(() => {
    if (cartLoading) {
      return;
    }

    scheduleStoreCartCTA();
  }, [cartLoading, scheduleStoreCartCTA, selectedStore.id]);

  // Hide checkout button when promo card (Tu tienda aquí) is selected
  useEffect(() => {
    if (promoCardActive) {
      hideStoreCartCTA();
    } else {
      scheduleStoreCartCTA();
    }
  }, [promoCardActive, hideStoreCartCTA, scheduleStoreCartCTA]);

  useEffect(() => {
    // Hide immediately when browser view changes; re-show when returning from Safari/BFCache.
    const rescheduleOnReturn = () => {
      if (document.hidden) return;
      requestAnimationFrame(() => {
        setTimeout(() => {
          scheduleStoreCartCTA();
        }, 80);
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hideStoreCartCTA();
        return;
      }
      rescheduleOnReturn();
    };

    const handlePageHide = () => {
      hideStoreCartCTA();
    };

    const handlePageShow = () => {
      rescheduleOnReturn();
    };

    const handleWindowFocus = () => {
      rescheduleOnReturn();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [hideStoreCartCTA, scheduleStoreCartCTA]);

  useEffect(() => {
    if (!isFirstFeedStore || storeViewMode === 'directory') {
      firstStoreHeaderPassedRef.current = true;
      setFirstStoreHeaderPassed(true);
      return;
    }

    firstStoreHeaderPassedRef.current = false;
    setFirstStoreHeaderPassed(false);

    const checkHeaderPassed = () => {
      const headerElement = storeHeaderRef.current;
      if (!headerElement) return;

      const rect = headerElement.getBoundingClientRect();
      const visibleHeaderOffset = Math.max(getStableHeaderOffset() + 8, 8);
      const passed = rect.bottom <= visibleHeaderOffset;

      if (passed === firstStoreHeaderPassedRef.current) return;

      firstStoreHeaderPassedRef.current = passed;
      setFirstStoreHeaderPassed(passed);
    };

    checkHeaderPassed();
    window.addEventListener('scroll', checkHeaderPassed, { passive: true });
    window.addEventListener('resize', checkHeaderPassed);

    return () => {
      window.removeEventListener('scroll', checkHeaderPassed);
      window.removeEventListener('resize', checkHeaderPassed);
    };
  }, [getStableHeaderOffset, isFirstFeedStore, storeViewMode]);

  useEffect(() => {
    if (!isFirstFeedStore || storeViewMode === 'directory') return;

    if (!firstStoreHeaderPassed) {
      hideStoreCartCTA();
      return;
    }

    scheduleStoreCartCTA();
  }, [firstStoreHeaderPassed, hideStoreCartCTA, isFirstFeedStore, scheduleStoreCartCTA, storeViewMode]);

  // Hide CTA when store selector is scrolled back into view; show it again when scrolled away
  useEffect(() => {
    if (!isDesktop) {
      selectorVisibleRef.current = false;
      return;
    }

    const checkSelectorVisibility = () => {
      const el = nearbySelectorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const visibleOffset = getStableHeaderOffset();
      // Selector is "visible" when its bottom is below the header (i.e. user can see it)
      const isVisible = rect.bottom > visibleOffset + 8;
      if (isVisible === selectorVisibleRef.current) return;
      selectorVisibleRef.current = isVisible;
      if (isVisible) {
        hideStoreCartCTA();
      } else {
        scheduleStoreCartCTA();
      }
    };

    checkSelectorVisibility();
    window.addEventListener('scroll', checkSelectorVisibility, { passive: true });
    window.addEventListener('resize', checkSelectorVisibility);
    return () => {
      window.removeEventListener('scroll', checkSelectorVisibility);
      window.removeEventListener('resize', checkSelectorVisibility);
    };
  }, [getStableHeaderOffset, hideStoreCartCTA, isDesktop, scheduleStoreCartCTA]);

  useEffect(() => {
    if (!storeCtaVisibleRef.current) return;
    if (cartDataStoreId !== selectedStore.id) {
      hideStoreCartCTA();
      return;
    }
    if (cartLoading) {
      return;
    }
    if (isFirstFeedStore && storeViewMode !== 'directory' && !firstStoreHeaderPassedRef.current) {
      hideStoreCartCTA();
      return;
    }
    const summary = getValidatedStoreCartSummary();
    const activeMeta = getActiveSharedMetaForStore(selectedStore.id || '');
    emitStoreCartCTA(true, summary.units, summary.total, selectedStore.name || '', selectedStore.id || '', activeMeta);
  }, [cartDataStoreId, cartItems, cartLoading, emitStoreCartCTA, getActiveSharedMetaForStore, getValidatedStoreCartSummary, hideStoreCartCTA, isFirstFeedStore, selectedStore.id, selectedStore.name, storeViewMode]);

  useEffect(() => {
    setPreferredStoreId(selectedStore.id || null);
    setPreferredStoreName(selectedStore.name || null);
  }, [selectedStore.id, selectedStore.name, setPreferredStoreId, setPreferredStoreName]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryStoreId = params.get('storeId')?.trim();
    const queryStoreSlug = params.get('storeSlug')?.trim();

    if (!queryStoreId) return;

    setSelectedStore((prev) => {
      if (queryStoreId === prev.id) {
        return prev;
      }

      return {
        ...DEFAULT_MINIBID_STORE,
        id: queryStoreId,
        name: 'Tienda',
        slug: queryStoreSlug || '',
      };
    });
    setInitialFeedStoreId((prev) => prev ?? queryStoreId);
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryStoreId = params.get('storeId')?.trim();
    const queryStoreSlug = params.get('storeSlug')?.trim();

    if (queryStoreId || !queryStoreSlug) return;

    let cancelled = false;

    const resolveStoreFromSlug = async () => {
      try {
        const store = await getStoreBySlug(queryStoreSlug);
        if (cancelled || !store?.id) return;

        setSelectedStore((prev) => ({
          ...prev,
          id: store.id,
          name: store.name || prev.name || 'Tienda',
          slug: store.slug || queryStoreSlug,
          avatar_url: store.avatar_url ?? prev.avatar_url ?? null,
          latitude: Number(store.latitude ?? prev.latitude ?? 0),
          longitude: Number(store.longitude ?? prev.longitude ?? 0),
        }));
        setInitialFeedStoreId((prev) => prev ?? store.id);
      } catch (error) {
        console.error('[Minibid24hsPage] Error resolving storeSlug:', error);
      }
    };

    void resolveStoreFromSlug();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    if (!selectedStore.id || typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const canonicalStoreSlug = String((storeDetails as any)?.slug || selectedStore.slug || '').trim();

    if (params.get('storeId') !== selectedStore.id) {
      params.set('storeId', selectedStore.id);
    }

    if (canonicalStoreSlug) {
      params.set('storeSlug', canonicalStoreSlug);
    } else {
      params.delete('storeSlug');
    }

    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || ''}`;
    const currentUrl = `${location.pathname}${location.search}${location.hash || ''}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', nextUrl);
    }

    try {
      localStorage.setItem(MINIBID_LAST_STORE_STORAGE_KEY, selectedStore.id);
    } catch {
      // Ignore storage errors (private mode or disabled storage).
    }
  }, [location.hash, location.pathname, location.search, selectedStore.id, selectedStore.slug, storeDetails]);

  // Cargar canasto actual (temporal si no logueado, BD si logueado)
  const loadCart = async (storeIdParam: string = selectedStore.id) => {
    try {
      setCartLoading(true);
      if (session?.user?.id) {
        const collaborative = getActiveCollaborativeMetaForStore(storeIdParam, { search: location.search });
        const activeCartId = collaborative.activeCartId;

        if (activeCartId && activeCartId.startsWith('shared-cart-')) {
          const sharedCartId = activeCartId.replace('shared-cart-', '');
          const sharedWithMe = await getCartsSharedWithMe();
          const sharedCart = (sharedWithMe || []).find((cart: any) => cart?.id === sharedCartId)
            || (sharedWithMe || []).find((cart: any) => cart?.store_id === storeIdParam);

          if (activeStoreIdRef.current !== storeIdParam) return;

          if (sharedCart) {
            const mappedSharedItems = (sharedCart.items || []).map((item: any) => ({
              id: `shared-${sharedCart.id}-${item.product_id}`,
              product_id: item.product_id,
              quantity: Number(item.quantity) || 0,
              unit_price: Number(item.unit_price) || 0,
            }));
            setCartItems(mappedSharedItems);
            setCartDataStoreId(storeIdParam);
            return;
          }
        }

        const openCarts = await getOpenCartsForUser(session.user.id);
        const cartsForStore = (openCarts || []).filter((cart: any) => cart?.store_id === storeIdParam);
        const preferredCartId = getActiveCartPreference(storeIdParam);
        const preferredCart = preferredCartId
          ? cartsForStore.find((cart: any) => cart?.id === preferredCartId)
          : null;
        const fallbackCart = cartsForStore[0] || await getCartForUser(session.user.id, storeIdParam);
        const selectedCart = preferredCart || fallbackCart;

        if (activeStoreIdRef.current !== storeIdParam) return;
        setCartItems(selectedCart?.cart_items || []);
        setCartDataStoreId(storeIdParam);
      } else {
        // Usuario no logueado: cargar canasto temporal
        const tempItems = getTempCartAsItems(storeIdParam);
        if (activeStoreIdRef.current !== storeIdParam) return;
        setCartItems(tempItems);
        setCartDataStoreId(storeIdParam);
      }
    } catch (err) {
      console.error('Error cargando canasto:', err);
      if (activeStoreIdRef.current === storeIdParam) {
        setCartDataStoreId(null);
      }
    } finally {
      if (activeStoreIdRef.current === storeIdParam) {
        setCartLoading(false);
      }
    }
  };

  // Cargar productos al montar
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('products')
          .select('id, title, description, fixed_sale_price, stock, main_image_url, cover_url, image_urls, is_auction, product_type, groceries_subcategory, current_bid, starting_bid, buy_now_price, store_id')
          .eq('store_id', selectedStore.id)
          .eq('status', 'available')
          .order('title', { ascending: true });

        if (error) throw error;
        setProducts(data || []);
      } catch (err) {
        console.error('Error cargando productos:', err);
        toast({
          title: '❌ Error',
          description: 'No se pudieron cargar los productos',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [toast, selectedStore.id]);

  // Cargar canasto al montar la página
  useEffect(() => {
    void loadCart(selectedStore.id);
  }, [session?.user?.id, selectedStore.id]);

  useEffect(() => {
    if (storesCarouselLoading) {
      setStoreMetaLoading(false);
      return;
    }

    let cancelled = false;

    const loadStoreMeta = async () => {
      setStoreMetaLoading(true);
      try {
        const storeWithMembers = await getStoreByIdWithMembers(selectedStore.id);
        const storeData = storeWithMembers
          ? {
              id: storeWithMembers.id,
              name: storeWithMembers.name,
              slug: storeWithMembers.slug,
              description: storeWithMembers.description,
              avatar_url: storeWithMembers.avatar_url,
              status: storeWithMembers.status,
              is_public: storeWithMembers.is_public,
              location_display_name: storeWithMembers.location_display_name,
              owner_id: storeWithMembers.owner_id,
              schedule: storeWithMembers.schedule,
              timezone: storeWithMembers.timezone,
              schedule_timezone: storeWithMembers.schedule_timezone,
            }
          : {
              id: selectedStore.id,
              name: selectedStore.name,
              slug: selectedStore.slug,
              avatar_url: selectedStore.avatar_url,
            };

        if (!cancelled) {
          setStoreDetails(storeData as StoreDetails);
        }

        try {
          const addresses = await getStoreAddresses(selectedStore.id);
          if (!cancelled) {
            setStoreAddresses(addresses || []);
          }
        } catch {
          if (!cancelled) {
            setStoreAddresses([]);
          }
        }

        const members = (storeWithMembers?.store_members || []).slice(0, 8);
        let mergedTeam: StoreTeamMember[] = [];

        if (members.length > 0) {
          const userIds = members.map((m: any) => m.user_id).filter(Boolean);
          const profiles = userIds.length > 0 ? await getProfilesByIds(userIds) : [];
          const profileMap = new Map(profiles.map((p: any) => [p.id, p]));

          mergedTeam = members.map((member: any) => ({
            user_id: member.user_id,
            role: member.role || 'staff',
            display_name:
              profileMap.get(member.user_id)?.display_name ||
              profileMap.get(member.user_id)?.username ||
              'Miembro',
            username: profileMap.get(member.user_id)?.username || null,
            avatar_url: profileMap.get(member.user_id)?.avatar_url || null,
            verification_status: profileMap.get(member.user_id)?.verification_status,
          }));
        }

        // Robust fallback: if store_members is partial (RLS) or empty, complement with owner/sellers.
        const knownIds = new Set(mergedTeam.map((m) => m.user_id).filter(Boolean));
        const fallbackIds = new Set<string>();

        if ((storeData as StoreDetails).owner_id) {
          fallbackIds.add((storeData as StoreDetails).owner_id as string);
        }

        const { data: sellerRows } = await supabase
          .from('products')
          .select('seller_id')
          .eq('store_id', selectedStore.id)
          .not('seller_id', 'is', null)
          .limit(8);

        (sellerRows || []).forEach((row: any) => {
          if (row?.seller_id) fallbackIds.add(row.seller_id);
        });

        const missingIds = Array.from(fallbackIds).filter((id) => !knownIds.has(id));
        if (missingIds.length > 0) {
          const fallbackProfiles = await getProfilesByIds(missingIds);
          const inferredMembers = fallbackProfiles.map((profile: any) => ({
            user_id: profile.id,
            role: profile.id === (storeData as StoreDetails).owner_id ? 'owner' : 'staff',
            display_name: profile.display_name || profile.username || 'Miembro',
            username: profile.username || null,
            avatar_url: profile.avatar_url || null,
            verification_status: profile.verification_status,
          }));

          mergedTeam = [...mergedTeam, ...inferredMembers].slice(0, 8);
        }

        if (!cancelled) setStoreTeam(mergedTeam);
      } catch {
        if (!cancelled) {
          setStoreTeam([]);
          setStoreDetails({
            id: selectedStore.id,
            name: selectedStore.name,
            slug: selectedStore.slug,
            avatar_url: selectedStore.avatar_url,
          });
          setStoreAddresses([]);
        }
      } finally {
        if (!cancelled) setStoreMetaLoading(false);
      }
    };

    void loadStoreMeta();

    return () => {
      cancelled = true;
    };
  }, [selectedStore.avatar_url, selectedStore.id, selectedStore.name, selectedStore.slug, storesCarouselLoading]);

  useEffect(() => {
    let cancelled = false;

    const loadChatRecipients = async () => {
      const activeStore = storeDetails || selectedStore;

      if (!activeStore?.id) {
        if (!cancelled) setStoreChatMemberIds([]);
        return;
      }

      const fallbackMemberIds = Array.from(new Set([
        ...(activeStore.owner_id ? [activeStore.owner_id] : []),
        ...storeTeam.map((member) => member.user_id),
      ].filter((userId) => Boolean(userId)))) as string[];

      if (!session?.user?.id) {
        if (!cancelled) {
          setStoreChatMemberIds(fallbackMemberIds);
          setStoreChatMembersLoading(false);
        }
        return;
      }

      setStoreChatMembersLoading(true);
      try {
        const resolvedIds = await resolveStoreOfficeChatRecipientIds({
          storeId: activeStore.id,
          fallbackMemberIds,
          currentUserId: session.user.id,
        });
        if (!cancelled) setStoreChatMemberIds(resolvedIds);
      } catch (error) {
        console.error('Error loading store office chat recipients:', error);
        if (!cancelled) {
          setStoreChatMemberIds(fallbackMemberIds.filter((id) => id !== session.user.id));
        }
      } finally {
        if (!cancelled) setStoreChatMembersLoading(false);
      }
    };

    void loadChatRecipients();

    return () => {
      cancelled = true;
    };
  }, [storeDetails, selectedStore, storeTeam, session?.user?.id]);

  // Auto-importar canasto local al iniciar sesión para que las cards reflejen cantidades al instante
  useEffect(() => {
    if (!session?.user?.id) {
      autoSyncAttemptedForUserRef.current = null;
      return;
    }

    if (autoSyncAttemptedForUserRef.current === session.user.id) return;
    autoSyncAttemptedForUserRef.current = session.user.id;

    let cancelled = false;

    const autoSyncLocalCart = async () => {
      try {
        const raw = localStorage.getItem('temp-cart-minibid24hs');
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!parsed?.items || parsed.items.length === 0) return;

        setCartSyncing(true);
        const result = await syncLocalCartToDB();
        if (cancelled) return;

        if (result.success && result.count && result.count > 0 && result.syncedRecords && result.backupItems && result.storeId) {
          window.dispatchEvent(new CustomEvent('cart-sync-metadata', {
            detail: {
              syncedRecords: result.syncedRecords,
              backupItems: result.backupItems,
              storeId: result.storeId,
              isUndoing: false,
            },
          }));
          window.dispatchEvent(new Event('cart-synced'));
        }
      } catch (err) {
        console.error('[Minibid24hsPage] Error auto-syncing local cart after login:', err);
      } finally {
        if (!cancelled) setCartSyncing(false);
      }
    };

    void autoSyncLocalCart();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, syncLocalCartToDB]);

  const emitLocalCartUpdated = useCallback(() => {
    window.dispatchEvent(new CustomEvent('cart-updated', {
      detail: { source: 'minibid-local' },
    }));
  }, []);

  const loadSharedCartActivity = useCallback(async () => {
    if (!session?.user?.id) return;

    const { isShared: sharedActive, activeCartId } = getActiveSharedMetaForStore(selectedStore.id);
    if (!sharedActive || !activeCartId || !activeCartId.startsWith('shared-cart-')) {
      setSharedActivityLogs([]);
      return;
    }

    const sharedCartId = activeCartId.replace('shared-cart-', '');
    const { data: logsData, error: logsError } = await supabase
      .from('shared_cart_activity_logs')
      .select('id, shared_cart_id, actor_user_id, action, product_id, product_title, quantity_before, quantity_after, quantity_delta, created_at')
      .eq('shared_cart_id', sharedCartId)
      .order('created_at', { ascending: false })
      .limit(40);

    if (logsError) {
      console.error('[Minibid24hsPage] Error loading shared cart activity logs:', logsError);
      setSharedActivityLogs([]);
      return;
    }

    const nextLogs = (logsData || []) as SharedCartActivityLog[];
    setSharedActivityLogs(nextLogs);

    const actorIds = Array.from(new Set(
      nextLogs
        .map((log) => log?.actor_user_id)
        .filter((actorId): actorId is string => Boolean(actorId))
    ));

    const unresolvedActorIds = actorIds.filter((actorId) => !sharedActivityActorNameByUserId[actorId]);
    if (unresolvedActorIds.length > 0) {
      try {
        const actorProfiles = await getProfilesByIds(unresolvedActorIds);
        const nextActorNames: Record<string, string> = {};
        (actorProfiles || []).forEach((profile: any) => {
          if (!profile?.id) return;
          nextActorNames[profile.id] = profile.display_name || profile.username || 'Alguien';
        });
        if (Object.keys(nextActorNames).length > 0) {
          setSharedActivityActorNameByUserId((prev) => ({ ...prev, ...nextActorNames }));
        }
      } catch (actorError) {
        console.error('[Minibid24hsPage] Error loading shared activity actors:', actorError);
      }
    }
  }, [session?.user?.id, selectedStore.id, getActiveSharedMetaForStore, sharedActivityActorNameByUserId]);

  // Escuchar cambios del canasto desde CartSidebar
  useEffect(() => {
    const handleCartUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ source?: string }>;
      if (customEvent.detail?.source === 'minibid-local') return;
      void loadCart(selectedStore.id);
    };
    const handleCartSynced = () => {
      // Recargar inmediatamente al importar canasto
      void loadCart(selectedStore.id);
    };
    const handleActiveCartChanged = () => {
      void loadCart(selectedStore.id);
    };
    window.addEventListener('cart-updated', handleCartUpdate);
    window.addEventListener('cart-synced', handleCartSynced);
    window.addEventListener('cart-active-changed', handleActiveCartChanged);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
      window.removeEventListener('cart-synced', handleCartSynced);
      window.removeEventListener('cart-active-changed', handleActiveCartChanged);
    };
  }, [selectedStore.id]);

  // Realtime collaboration: listen shared cart item changes from other users.
  useEffect(() => {
    if (!session?.user?.id) return;

    const { isShared: sharedActive } = getActiveSharedMetaForStore(selectedStore.id);
    if (!sharedActive) return;

    // Shared carts realtime subscriptions require new architecture
    // This would need to be updated to use the new cart_access table
    return () => {
      // cleanup function
    };
  }, [session?.user?.id, selectedStore.id, location.search, getActiveSharedMetaForStore]);

  // Load collaborative activity logs
  useEffect(() => {
    void loadSharedCartActivity();
  }, [loadSharedCartActivity]);

  // Realtime collaboration: listen shared cart activity logs from other users.
  useEffect(() => {
    if (!session?.user?.id) return;

    const { isShared: sharedActive } = getActiveSharedMetaForStore(selectedStore.id);
    if (!sharedActive) return;

    // Shared carts activity subscriptions require new architecture
    // This would need to be updated to use the new cart_access table
    return () => {
      // cleanup function
    };
  }, [session?.user?.id, selectedStore.id, getActiveSharedMetaForStore, loadSharedCartActivity]);

  // Agregar producto al canasto (optimista en la primera adición)
  const addToCart = async (product: Product, quantityToAdd: number = 1) => {
    const { isShared: sharedActive } = getActiveSharedMetaForStore(selectedStore.id);

    if (session?.user?.id) {
      setUpdatingProductIds((prev) => new Set([...prev, product.id]));
      setCartSyncing(true);
      try {
        // With new direct-access architecture, shared carts use the user's own cart
        // No token-based operations needed
        await addCartItem(session.user.id, product.id, quantityToAdd);
        await loadCart(selectedStore.id);
        emitLocalCartUpdated();
      } catch (err: any) {
        console.error('Error sincronizando canasto compartido:', err);
        toast({
          title: '❌ Error',
          description: err?.message || 'No se pudo guardar el producto',
          variant: 'destructive',
        });
      } finally {
        setUpdatingProductIds((prev) => {
          const updated = new Set(prev);
          updated.delete(product.id);
          return updated;
        });
        setCartSyncing(false);
      }
      return;
    }

    // Marcar el item como actualizándose INMEDIATAMENTE
    setUpdatingProductIds((prev) => new Set([...prev, product.id]));

    // Actualización optimista - agregar inmediatamente a la UI
    const newItem: CartItem = {
      id: `temp-${product.id}-${Date.now()}`,
      product_id: product.id,
      quantity: quantityToAdd,
      unit_price: product.fixed_sale_price,
    };

    const existingItem = cartItems.find((item) => item.product_id === product.id);
    let finalItem: CartItem;

    if (existingItem) {
      // Item ya existe, sumar la cantidad
      finalItem = { ...existingItem, quantity: existingItem.quantity + quantityToAdd };
      setCartItems((prev) =>
        prev.map((item) =>
          item.product_id === product.id
            ? finalItem
            : item
        )
      );
    } else {
      // Item nuevo, agregarlo
      finalItem = newItem;
      setCartItems((prev) => [...prev, newItem]);
    }

    setCartSyncing(true);

    try {
      if (session?.user?.id) {
        // Usuario logueado: persistir y reconciliar solo el item afectado.
        const persistedItem: any = await addCartItem(session.user.id, product.id, quantityToAdd);

        setCartItems((prev) => {
          const targetIndex = prev.findIndex((item) => item.product_id === product.id);
          const reconciledItem: CartItem = {
            id: persistedItem?.id || prev[targetIndex]?.id || `temp-${product.id}`,
            product_id: product.id,
            quantity: Number.isFinite(persistedItem?.quantity)
              ? persistedItem.quantity
              : (targetIndex >= 0 ? prev[targetIndex].quantity : quantityToAdd),
            unit_price: persistedItem?.unit_price || product.fixed_sale_price,
          };

          if (targetIndex === -1) {
            return [...prev, reconciledItem];
          }

          return prev.map((item, index) => (index === targetIndex ? reconciledItem : item));
        });
      } else {
        // Usuario no logueado: persistencia local sin recarga completa.
        addToTempCart(product, quantityToAdd, selectedStore.id);
      }

      emitLocalCartUpdated();
    } catch (err: any) {
      console.error('Error sincronizando canasto:', err);
      // Revertir cambio local en caso de error
      setCartItems((prev) =>
        existingItem
          ? prev.map((item) =>
              item.product_id === product.id
                ? { ...item, quantity: existingItem.quantity }
                : item
            )
          : prev.filter((item) => item.product_id !== product.id)
      );
      emitLocalCartUpdated();
      toast({
        title: '❌ Error',
        description: 'No se pudo guardar el producto',
        variant: 'destructive',
      });
    } finally {
      // Desmarcar el item como actualizándose SOLO después de que el estado local
      // tenga el ID real de la base de datos
      setUpdatingProductIds((prev) => {
        const updated = new Set(prev);
        updated.delete(product.id);
        return updated;
      });
      setCartSyncing(false);
    }
  };

  // Actualizar cantidad (confirmada, sin optimismo visual)
  const updateQuantity = async (cartItemId: string, quantityCalculator: (current: number) => number) => {
    const currentItem = cartItems.find((item) => item.id === cartItemId);
    if (!currentItem) return;

    const finalQuantity = quantityCalculator(currentItem.quantity);
    if (finalQuantity === currentItem.quantity) return;

    // Marcar el product como actualizándose
    setUpdatingProductIds((prev) => new Set([...prev, currentItem.product_id]));
    setCartSyncing(true);

    try {
      if (finalQuantity <= 0) {
        if (session?.user?.id) {
          await removeCartItem(cartItemId);
        } else if (currentItem.product_id) {
          removeFromTempCart(currentItem.product_id);
        }
      } else {
        if (session?.user?.id) {
          await updateCartItem(cartItemId, finalQuantity);
        } else if (currentItem.product_id) {
          updateTempCartItemQuantity(currentItem.product_id, finalQuantity);
        }
      }

      await loadCart(selectedStore.id);
      emitLocalCartUpdated();
    } catch (err: any) {
      console.error('Error actualizando cantidad:', err);
      toast({
        title: '❌ Error',
        description: 'No se pudo actualizar la cantidad',
        variant: 'destructive',
      });
    } finally {
      setUpdatingProductIds((prev) => {
        const updated = new Set(prev);
        updated.delete(currentItem.product_id);
        return updated;
      });
      setCartSyncing(false);
    }
  };

  // Remover del canasto (carga segura - esperar confirmación del servidor)
  const removeFromCart = async (cartItemId: string) => {
    const itemToRemove = cartItems.find((item) => item.id === cartItemId);

    setCartSyncing(true);
    try {
      if (session?.user?.id) {
        // Usuario logueado: remover con permisos directos (personal o colaborativo).
        await removeCartItem(cartItemId);
      } else {
        // Usuario no logueado: remover del canasto temporal
        if (itemToRemove) {
          removeFromTempCart(itemToRemove.product_id);
        }
      }

      // Recargar canasto después de confirmar remoción
      await loadCart();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (err: any) {
      console.error('Error removiendo del canasto:', err);
      toast({
        title: '❌ Error',
        description: 'No se pudo remover el producto',
        variant: 'destructive',
      });
    } finally {
      setCartSyncing(false);
    }
  };

  // Abrir sidebar del canasto (o prompt de login si está vacío)
  const handleOpenCart = async () => {
    if (!session?.user?.id) {
      // Si no está logueado pero tiene items en canasto temporal, permitir ver
      const tempItems = getTempCartAsItems(selectedStore.id);
      if (tempItems.length === 0) {
        toast({
          title: '⚠️ Canasto vacío',
          description: 'Agrega productos para verlos aquí',
          variant: 'destructive',
        });
        return;
      }
      // Tiene items en canasto temporal, mostrar checkout
    }

    if (favoritesSidebarOpen) {
      closeFavoritesSidebar();
    }

    await loadCart();
    openCheckoutSidebar({ cartOnly: true });
  };

  const handleRentStoreClick = useCallback(async () => {
    if (rentStoreLoading) return;

    setRentStoreLoading(true);
    try {
      const selectColumns = 'id, store_id, title, description, fixed_sale_price, fixed_rental_price, stock, main_image_url, cover_url, image_urls, details';
      const candidateStoreIds = Array.from(new Set([DEFAULT_MINIBID_STORE.id, selectedStore.id].filter(Boolean)));

      const scoreRentalCandidate = (product: Product): number => {
        const details = (product.details && typeof product.details === 'object') ? product.details : {};
        const title = String(product.title || '').toLowerCase();
        const description = String(product.description || '').toLowerCase();
        const rentalTarget = String((details as any).rental_target || '').toLowerCase();
        const billingMode = String((details as any).billing_mode || '').toLowerCase();

        let score = 0;
        if ((details as any).is_store_rental === true) score += 10;
        if (billingMode === 'per_day') score += 5;
        if (rentalTarget.includes('store') || rentalTarget.includes('tienda')) score += 5;
        if (title.includes('alquilar tienda') || title.includes('alquiler tienda')) score += 4;
        if (title.includes('rental')) score += 3;
        if (description.includes('tienda') || description.includes('store')) score += 2;
        return score;
      };

      let rentalProduct: Product | null = null;

      if (candidateStoreIds.length > 0) {
        const localCandidatesQuery = await supabase
          .from('products')
          .select(selectColumns)
          .in('store_id', candidateStoreIds)
          .eq('status', 'available')
          .limit(200);

        const localCandidates = (localCandidatesQuery.data as Product[] | null) || [];
        const bestLocal = localCandidates
          .map((item) => ({ item, score: scoreRentalCandidate(item) }))
          .sort((a, b) => b.score - a.score)[0];

        if (bestLocal && bestLocal.score > 0) {
          rentalProduct = bestLocal.item;
        }
      }

      if (!rentalProduct) {
        const globalFallbackQuery = await supabase
          .from('products')
          .select(selectColumns)
          .eq('status', 'available')
          .or('title.ilike.%alquilar%,title.ilike.%alquiler%,title.ilike.%rental%,description.ilike.%tienda%')
          .limit(200);

        const globalCandidates = (globalFallbackQuery.data as Product[] | null) || [];
        const bestGlobal = globalCandidates
          .map((item) => ({ item, score: scoreRentalCandidate(item) }))
          .sort((a, b) => b.score - a.score)[0];

        if (bestGlobal && bestGlobal.score > 0) {
          rentalProduct = bestGlobal.item;
        }
      }

      if (!rentalProduct) {
        toast({
          title: 'Producto no encontrado',
          description: 'No encontramos el producto de alquiler de tienda en Minibid.',
          variant: 'destructive',
        });
        return;
      }

      const unitPrice = Number(rentalProduct.fixed_sale_price ?? rentalProduct.fixed_rental_price ?? 25000) || 25000;
      const normalizedProduct: Product = {
        ...rentalProduct,
        fixed_sale_price: unitPrice,
      };
      const rentalStoreId = normalizedProduct.store_id || DEFAULT_MINIBID_STORE.id;

      if (session?.user?.id) {
        await addCartItem(session.user.id, normalizedProduct.id, 1);
      } else {
        addToTempCart(normalizedProduct, 1, rentalStoreId);
      }

      setPreferredStoreId(rentalStoreId);
      setPreferredStoreName(rentalStoreId === DEFAULT_MINIBID_STORE.id ? DEFAULT_MINIBID_STORE.name : (selectedStore.name || 'Tienda'));
      openCheckoutSidebar({ cartOnly: true, preferredStoreId: rentalStoreId });
      window.dispatchEvent(new CustomEvent('cart-updated'));

    } catch (error) {
      console.error('[Minibid24hsPage] Error adding store rental product:', error);
      toast({
        title: 'Error',
        description: 'No se pudo agregar el alquiler de tienda al canasto.',
        variant: 'destructive',
      });
    } finally {
      setRentStoreLoading(false);
    }
  }, [openCheckoutSidebar, rentStoreLoading, selectedStore.id, selectedStore.name, session?.user?.id, setPreferredStoreId, setPreferredStoreName, toast]);

  // Track max card height for equalization across all aisles
  const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null);
  const effectiveStore = storeDetails || selectedStore;
  const groupedKeywordAisles = React.useMemo(() => groupProductsByKeywordAisles(products), [products]);
  const normalizedSelectedAisle = React.useMemo(
    () => selectedAisle?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') ?? null,
    [selectedAisle]
  );
  const filteredAisleGroups = React.useMemo(
    () => groupedKeywordAisles.filter((aisleGroup) => !normalizedSelectedAisle || aisleGroup.aisle.id === normalizedSelectedAisle),
    [groupedKeywordAisles, normalizedSelectedAisle]
  );
  const keywordAisles = React.useMemo(() => groupedKeywordAisles.map((group) => group.aisle), [groupedKeywordAisles]);
  const keywordAislesScrollRef = useRef<HTMLDivElement>(null);
  const [showKeywordAislesLeftFade, setShowKeywordAislesLeftFade] = useState(false);
  const [showKeywordAislesRightFade, setShowKeywordAislesRightFade] = useState(false);
  const KEYWORD_AISLE_FADE_THRESHOLD_PX = 8;

  const syncKeywordAislesFadeState = useCallback(() => {
    const element = keywordAislesScrollRef.current;
    if (!element) {
      setShowKeywordAislesLeftFade(false);
      setShowKeywordAislesRightFade(false);
      return;
    }

    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    const hasOverflow = maxScrollLeft > KEYWORD_AISLE_FADE_THRESHOLD_PX;

    setShowKeywordAislesLeftFade(hasOverflow && element.scrollLeft > KEYWORD_AISLE_FADE_THRESHOLD_PX);
    setShowKeywordAislesRightFade(hasOverflow && element.scrollLeft < maxScrollLeft - KEYWORD_AISLE_FADE_THRESHOLD_PX);
  }, []);

  useLayoutEffect(() => {
    syncKeywordAislesFadeState();
  }, [syncKeywordAislesFadeState, keywordAisles.length, selectedAisle, storeViewMode, viewportWidth]);

  const visibleTeam = storeTeam.slice(0, 4);
  const extraTeamCount = Math.max(0, storeTeam.length - visibleTeam.length);
  const storeDescription = stripHtml((effectiveStore as any).description || '').trim();
  const fayeSplashStoreAddress = React.useMemo(() => {
    const firstAddress = storeAddresses[0]?.location?.displayName;
    if (firstAddress) return formatAddressForDisplay(firstAddress);

    const fallbackAddress = (effectiveStore as any).location_display_name;
    return fallbackAddress ? formatAddressForDisplay(fallbackAddress) : null;
  }, [effectiveStore, storeAddresses]);
  const deliveryAvailable =
    (effectiveStore as any).delivery_available ??
    (effectiveStore as any).delivery_enabled ??
    (effectiveStore as any).supports_delivery ??
    (effectiveStore as any).accepts_delivery ??
    true;
  const pickupAvailable =
    (effectiveStore as any).pickup_available ??
    (effectiveStore as any).pickup_enabled ??
    (effectiveStore as any).supports_pickup ??
    (effectiveStore as any).accepts_pickup ??
    true;

  const isCurrentUserOnStoreTeam = Boolean(
    session?.user?.id && (
      (effectiveStore as any).owner_id === session.user.id ||
      storeTeam.some((member) => member.user_id === session.user.id)
    )
  );
  const storeDashboardPath = effectiveStore.slug
    ? `/${effectiveStore.slug}/tablero`
    : `/tiendas/cargar?storeId=${effectiveStore.id}`;
  const handleOpenTeamMemberProfile = useCallback((member: StoreTeamMember) => {
    setSelectedTeamMember(member);
    setTeamProfileSidebarOpen(true);
  }, []);

  useEffect(() => {
    const section = catalogSectionRef.current;
    if (!section) return;

    if (storeViewMode === 'directory') {
      setCatalogInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setCatalogInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '350px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [storeViewMode, storesCarouselLoading]);

  useEffect(() => {
    let cancelled = false;

    const loadTeamProfile = async () => {
      if (!teamProfileSidebarOpen || !selectedTeamMember?.user_id) {
        if (!cancelled) {
          setSelectedTeamProfile(null);
          setSelectedTeamProfileLoading(false);
          setSelectedTeamNotes([]);
          setSelectedTeamNotesLoading(false);
          setSelectedTeamNotesVisibleCount(TEAM_ACTIVITY_PAGE_SIZE);
        }
        return;
      }

      setSelectedTeamProfileLoading(true);
      setSelectedTeamNotesLoading(true);
      setSelectedTeamNotesVisibleCount(TEAM_ACTIVITY_PAGE_SIZE);
      try {
        const profile = await getProfileById(selectedTeamMember.user_id);
        const notes = await getUserNotesByUserId(selectedTeamMember.user_id);
        if (!cancelled) {
          setSelectedTeamProfile(profile);
          const sortedNotes = [...(notes || [])].sort((a: any, b: any) =>
            new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
          );
          setSelectedTeamNotes(sortedNotes);
        }
      } catch {
        if (!cancelled) {
          setSelectedTeamProfile(null);
          setSelectedTeamNotes([]);
        }
      } finally {
        if (!cancelled) {
          setSelectedTeamProfileLoading(false);
          setSelectedTeamNotesLoading(false);
        }
      }
    };

    void loadTeamProfile();

    return () => {
      cancelled = true;
    };
  }, [teamProfileSidebarOpen, selectedTeamMember?.user_id]);

  const visibleTeamActivity = selectedTeamNotes.slice(0, selectedTeamNotesVisibleCount);

  const handleStartStoreGroupChat = useCallback(async () => {
    if (!effectiveStore?.id) {
      return;
    }

    if (!session?.user?.id) {
      const anonymousSessionId = getOrCreateAnonymousSupportSessionId(effectiveStore.id);
      openChatWindow({
        id: `help-anonymous-${effectiveStore.id}-${anonymousSessionId}`,
        type: 'dm',
        title: effectiveStore.name || 'Tienda',
        avatarUrl: effectiveStore.avatar_url || null,
        isAnonymous: true,
        anonymousSessionId,
        anonymousStoreId: effectiveStore.id,
      });
      return;
    }

    if (storeChatMemberIds.length === 0) {
      toast({
        title: 'Sin equipo disponible',
        description: 'Esta tienda no tiene miembros visibles para iniciar un chat grupal.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setStartingStoreChat(true);
      const groupDescription = getStoreGroupDescription(effectiveStore.id);
      const groupName = `${effectiveStore.name} · Equipo`;

      const existingGroups = await getUserGroupChats();
      const existingGroup = existingGroups.find(
        (group) => group.description === groupDescription || group.name === groupName
      );

      const groupId = existingGroup?.id || await createGroupChat({
        name: groupName,
        description: groupDescription,
        member_ids: storeChatMemberIds,
        avatar_url: effectiveStore.avatar_url || undefined,
      });

      if (session?.user?.id) {
        try {
          await setGroupChatStarter(groupId, session.user.id);
        } catch {
          // Non-blocking.
        }
      }

      if (existingGroup?.id && effectiveStore.avatar_url) {
        await supabase
          .from('group_chats')
          .update({ avatar_url: effectiveStore.avatar_url })
          .eq('id', groupId);
      }

      // Keep existing store groups aligned with current office-contact recipients.
      if (existingGroup?.id) {
        await Promise.all(
          storeChatMemberIds.map(async (memberId) => {
            try {
              await addGroupMember(groupId, memberId);
            } catch {
              // Ignore duplicate/no-permission errors to avoid blocking chat open.
            }
          })
        );
      }

      openChatWindow({
        id: `group-${groupId}`,
        type: 'group',
        groupId,
        title: groupName,
        avatarUrl: effectiveStore.avatar_url || null,
      });
    } catch (error: any) {
      console.error('Error starting store group chat:', error);
      toast({
        title: 'Error al iniciar chat grupal',
        description: error?.message || 'No se pudo abrir el chat del equipo de la tienda.',
        variant: 'destructive',
      });
    } finally {
      setStartingStoreChat(false);
    }
  }, [effectiveStore.avatar_url, effectiveStore.id, effectiveStore.name, session?.user?.id, storeChatMemberIds, toast, openChatWindow]);

  // Robust height equalization with ResizeObserver and image loading detection
  useEffect(() => {
    if (!catalogInView || storeViewMode === 'directory') return;

    let timeoutId: NodeJS.Timeout;
    let resizeObserver: ResizeObserver | null = null;

    const calculateAndSetMaxHeight = () => {
      const cards = document.querySelectorAll('[data-minibid-product-card]');
      if (cards.length === 0) return;

      // Wait for images to load
      const imagePromises = Array.from(cards).flatMap(card => {
        const images = (card as HTMLElement).querySelectorAll('img');
        return Array.from(images).map(img => {
          return new Promise<void>(resolve => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          });
        });
      });

      Promise.all(imagePromises).then(() => {
        // Get heights after images load
        const heights = Array.from(cards).map(card => {
          const elem = card as HTMLElement;
          return elem.scrollHeight; // Use scrollHeight for accurate measurement
        });

        const max = Math.max(...heights);
        if (max > 0 && max !== maxCardHeight) {
          setMaxCardHeight(max);
        }
      });
    };

    // Use ResizeObserver to detect when cards change
    resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      // Debounce recalculation
      timeoutId = setTimeout(calculateAndSetMaxHeight, 200);
    });

    // Observe all cards
    const cards = document.querySelectorAll('[data-minibid-product-card]');
    cards.forEach(card => resizeObserver?.observe(card as Element));

    // Initial calculation with delay to allow DOM to settle
    timeoutId = setTimeout(calculateAndSetMaxHeight, 100);

    // Also recalculate on window resize
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateAndSetMaxHeight, 300);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [catalogInView, cartItems.length, loading, maxCardHeight, products, storeViewMode, visibleAisleProductCounts]);

  const loadMoreAisleProducts = useCallback((aisleId: string, totalProducts: number) => {
    setVisibleAisleProductCounts((prev) => {
      const currentVisible = prev[aisleId] ?? CATALOG_INITIAL_RENDER_PER_AISLE;
      return {
        ...prev,
        [aisleId]: Math.min(totalProducts, currentVisible + CATALOG_RENDER_BATCH),
      };
    });
  }, []);

  const startStorePanning = useCallback(() => {
    if (products.length === 0) return;
    startProductPanning(products, { intervalMs: 4500, loop: true });
  }, [products, startProductPanning]);

  const preloadPanningProducts = useCallback((panningProducts: Product[]) => {
    const preloadTargets = panningProducts.slice(0, 2);
    if (preloadTargets.length === 0) return;
    void Promise.allSettled(preloadTargets.map((product) => getProductById(product.id)));
  }, []);

  const preloadAndOpenFirstPanningProduct = useCallback(async (panningProducts: Product[]) => {
    const firstProduct = panningProducts[0];
    if (!firstProduct) return;

    try {
      const fullProduct = await getProductById(firstProduct.id);
      if (!fullProduct) return;
      setSelectedProduct(fullProduct);
      setProductSidebarOpen(true);
    } catch (error) {
      console.warn('Error preloading first product for Faye splash:', error);
    }
  }, [setProductSidebarOpen, setSelectedProduct]);

  const handleStartStorePanning = useCallback(() => {
    if (products.length === 0) return;
    window.dispatchEvent(new CustomEvent('faye-mode-start'));
    pendingFayeAisleProductsRef.current = null;
    setFayeSplashAisleName(null);
    setFayeSplashStoreName((selectedStore.name || 'Tienda').trim() || 'Tienda');
    void preloadAndOpenFirstPanningProduct(products);
    preloadPanningProducts(products);
  }, [preloadAndOpenFirstPanningProduct, preloadPanningProducts, products, selectedStore.name]);

  const startAislePanning = useCallback((aisleProducts: Product[]) => {
    if (!aisleProducts.length) return;
    startProductPanning(aisleProducts, { intervalMs: 4500, loop: true });
  }, [startProductPanning]);

  const handleStartAislePanning = useCallback((aisleProducts: Product[], aisleName: string) => {
    if (!aisleProducts.length) return;
    window.dispatchEvent(new CustomEvent('faye-mode-start'));
    pendingFayeAisleProductsRef.current = aisleProducts;
    setFayeSplashStoreName((selectedStore.name || 'Tienda').trim() || 'Tienda');
    setFayeSplashAisleName((aisleName || 'Sección').trim() || 'Sección');
    void preloadAndOpenFirstPanningProduct(aisleProducts);
    preloadPanningProducts(aisleProducts);
  }, [preloadAndOpenFirstPanningProduct, preloadPanningProducts, selectedStore.name]);

  const handleFayeSplashComplete = useCallback(() => {
    const pendingAisleProducts = pendingFayeAisleProductsRef.current;
    pendingFayeAisleProductsRef.current = null;
    setFayeSplashStoreName(null);
    setFayeSplashAisleName(null);

    if (pendingAisleProducts?.length) {
      startAislePanning(pendingAisleProducts);
      return;
    }

    startStorePanning();
  }, [startAislePanning, startStorePanning]);

  const renderCatalogProductCardSkeleton = (key: string) => (
    <Card
      key={key}
      className="flex flex-col overflow-hidden border-primary/10 shadow-[0_4px_14px_hsl(var(--primary)/0.05),0_0_0_1px_hsl(var(--primary)/0.04)]"
    >
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="flex flex-1 flex-col gap-3 p-3">
        <div className="space-y-2">
          <Skeleton className="h-4 w-5/6 rounded" />
          <Skeleton className="h-3.5 w-2/3 rounded" />
          <Skeleton className="h-3.5 w-1/2 rounded" />
        </div>
        <div className="mt-auto flex items-center justify-between gap-3">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-9 w-24 rounded-full" />
        </div>
      </div>
    </Card>
  );

  const renderCatalogSectionSkeleton = (sectionKey: string) => (
    <div key={sectionKey} className="space-y-4">
      <div className="rounded-xl border border-primary/15 bg-[hsl(var(--primary)/0.06)] p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-2">
            <Skeleton className="h-7 w-40 rounded" />
            <Skeleton className="h-4 w-24 rounded" />
          </div>
          <Skeleton className="h-10 w-28 rounded-full shrink-0" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, cardIndex) => renderCatalogProductCardSkeleton(`${sectionKey}-card-${cardIndex}`))}
      </div>
    </div>
  );

  const renderProductCatalogSkeleton = () => (
    <div className="space-y-8" aria-hidden="true">
      <div className="flex items-center gap-3 min-w-0">
        <Skeleton className="h-10 w-36 rounded-full shrink-0" />
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pb-1">
          <Skeleton className="h-8 w-24 rounded-full shrink-0" />
          <Skeleton className="h-8 w-28 rounded-full shrink-0" />
          <Skeleton className="h-8 w-20 rounded-full shrink-0" />
          <Skeleton className="h-8 w-24 rounded-full shrink-0" />
        </div>
      </div>

      <div className="space-y-6">
        {['catalog-section-1', 'catalog-section-2'].map((sectionKey) => renderCatalogSectionSkeleton(sectionKey))}
      </div>
    </div>
  );

  const renderProductCatalog = () => (
    loading ? (
      renderProductCatalogSkeleton()
    ) : (!catalogInView && storeViewMode !== 'directory') ? (
      <div className="space-y-6">
        {[1, 2].map((section) => (
          <div key={section} className="space-y-4">
            <Skeleton className="h-20 w-full max-w-md rounded-lg" />
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((card) => (
                <Skeleton key={`${section}-${card}`} className="h-72" />
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="space-y-8">
        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="space-y-3">
              <div className="flex justify-center">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary">
                  <ChevronDown className="h-6 w-6" />
                </span>
              </div>
              <h3 className="text-xl font-semibold">No hay ofertas disponibles</h3>
              <p className="text-muted-foreground">Vuelve pronto, habrá nuevas ofertas en 24 horas</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <Button
                type="button"
                size="sm"
                onClick={handleStartStorePanning}
                className={`faye-nebula-btn h-10 shrink-0 rounded-full border border-primary/15 px-4 text-sm font-semibold transition-all duration-300 ${
                  productPanningActive
                    ? 'bg-primary/12 text-primary shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_0_18px_rgba(99,102,241,0.14)] hover:bg-primary/16'
                    : 'bg-muted/70 text-primary hover:bg-muted'
                }`}
              >
                <Play className="mr-2 h-4 w-4" />
                Recorrer tienda
              </Button>
              {keywordAisles.length > 0 && (
                <div className="relative min-w-0 flex-1">
                  <div
                    className={`pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-6 bg-gradient-to-r from-background via-background/90 to-transparent transition-opacity duration-150 ${showKeywordAislesLeftFade ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div
                    className={`pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-6 bg-gradient-to-l from-background via-background/90 to-transparent transition-opacity duration-150 ${showKeywordAislesRightFade ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div
                    ref={keywordAislesScrollRef}
                    onScroll={syncKeywordAislesFadeState}
                    className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-nowrap"
                  >
                    {keywordAisles.map((aisle) => {
                      const isActive = selectedAisle?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === aisle.id;
                      return (
                        <button
                          key={aisle.id}
                          type="button"
                          onClick={() => setSelectedAisle(isActive ? null : aisle.id)}
                          className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                            isActive
                              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                              : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                          }`}
                        >
                          {aisle.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <FayeStoreSplash
              open={fayeSplashStoreName !== null}
              storeName={fayeSplashStoreName || 'Tienda'}
              aisleName={fayeSplashAisleName}
              storeAddress={fayeSplashStoreAddress}
              onComplete={handleFayeSplashComplete}
            />
          {filteredAisleGroups
            .map((aisleGroup) => {
              const totalProducts = aisleGroup.products.length;
              const visibleCount = visibleAisleProductCounts[aisleGroup.aisle.id] ?? Math.min(CATALOG_INITIAL_RENDER_PER_AISLE, totalProducts);
              const visibleProducts = aisleGroup.products.slice(0, visibleCount);
              const hasMoreProducts = visibleCount < totalProducts;

              return (
              <div key={aisleGroup.aisle.id} className="space-y-4">
                <div className="p-4 rounded-xl border border-primary/20 bg-[hsl(var(--primary)/0.08)] backdrop-blur-md relative overflow-hidden shadow-sm">
                  <div className="flex items-center justify-between gap-3 relative z-10">
                    <div>
                      <h2 className="text-2xl font-bold">{aisleGroup.aisle.name}</h2>
                      <p className="text-sm text-muted-foreground">{aisleGroup.products.length} productos</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleStartAislePanning(aisleGroup.products, aisleGroup.aisle.name)}
                      className={`h-10 shrink-0 rounded-full border border-primary/15 px-4 text-sm font-semibold transition-all duration-300 ${
                        productPanningActive
                          ? 'bg-primary/12 text-primary shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_0_18px_rgba(99,102,241,0.14)] hover:bg-primary/16'
                          : 'bg-muted/70 text-primary hover:bg-muted'
                      }`}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Recorrer
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {visibleProducts.map((product) => {
                    const inCart = cartItems.find((item) => item.product_id === product.id);
                    const isUpdating = updatingProductIds.has(product.id);
                    const showQuantitySelector = inCart || isUpdating;
                    const imageUrl = product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) ? product.image_urls[0] : null);
                    return (
                      <Card
                        key={product.id}
                        className="transition-all duration-300 transform-gpu hover:scale-[1.012] flex flex-col overflow-hidden shadow-[0_4px_14px_hsl(var(--primary)/0.05),0_0_0_1px_hsl(var(--primary)/0.06)] hover:shadow-[0_10px_24px_hsl(var(--primary)/0.09),0_0_0_1px_hsl(var(--primary)/0.10)] dark:shadow-[0_6px_18px_hsl(var(--primary)/0.08),0_0_0_1px_hsl(var(--primary)/0.09)] dark:hover:shadow-[0_12px_28px_hsl(var(--primary)/0.12),0_0_0_1px_hsl(var(--primary)/0.14)]"
                        data-minibid-product-card
                        style={maxCardHeight ? { height: `${maxCardHeight}px` } : {}}
                      >
                        <div
                          className="relative bg-primary/10 aspect-square flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
                          onClick={async () => {
                            if (loadingProductId === product.id) return;
                            try {
                              setLoadingProductId(product.id);
                              startProductPanning(visibleProducts, {
                                intervalMs: 4500,
                                loop: true,
                                startIndex: visibleProducts.findIndex((p) => p.id === product.id),
                              });
                              pauseProductPanning();
                              const fullProduct = await getProductById(product.id);
                              if (fullProduct) { setSelectedProduct(fullProduct); setProductSidebarOpen(true); }
                            } catch (e) {
                              console.error('Error loading product:', e);
                            } finally {
                              setLoadingProductId(null);
                            }
                          }}
                        >
                          {imageUrl ? (
                            <img src={imageUrl} alt={product.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingCart className="h-12 w-12 text-primary/30" />
                            </div>
                          )}
                          <div className="absolute top-2 right-2 z-20">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                const wasNotFavorited = !isFavorited(product.id);
                                toggleFavorite({
                                  id: product.id,
                                  title: product.title,
                                  description: product.description,
                                  fixed_sale_price: product.fixed_sale_price,
                                  stock: product.stock,
                                  addedAt: Date.now(),
                                  store_id: selectedStore.id,
                                  store_name: selectedStore.name
                                });
                                playFavoriteSound(wasNotFavorited);
                                vibrate();
                              }}
                              size="icon"
                              variant="ghost"
                              className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square"
                            >
                              <Heart className={`h-5 w-5 transition-all ${isFavorited(product.id) ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                            </Button>
                          </div>
                          <div className="absolute bottom-2 right-2 z-20" onClick={(e) => e.stopPropagation()}>
                            {showQuantitySelector ? (
                              <QuantitySelector
                                key={`qty-${inCart?.id || 'new'}-${product.id}`}
                                cartItemId={inCart?.id || `temp-${product.id}`}
                                quantity={inCart?.quantity || 1}
                                onUpdateQuantity={updateQuantity}
                                onPlayBeep={playBeep}
                                onVibrate={vibrate}
                                disabled={isUpdating}
                              />
                            ) : (
                              <Button
                                onClick={(e) => { e.stopPropagation(); vibrate('add'); playBeep('add'); addToCart(product); }}
                                size="icon"
                                className="h-[43px] w-[43px] p-0 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 flex items-center justify-center shrink-0 aspect-square"
                              >
                                <Plus className="h-6 w-6" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <CardHeader className="pb-2 flex-1 pt-3">
                          <CardTitle
                            className="text-sm line-clamp-2 cursor-pointer hover:text-primary transition-colors hover:underline"
                            onClick={async () => {
                              if (loadingProductId === product.id) return;
                              try {
                                setLoadingProductId(product.id);
                                startProductPanning(visibleProducts, {
                                  intervalMs: 4500,
                                  loop: true,
                                  startIndex: visibleProducts.findIndex((p) => p.id === product.id),
                                });
                                pauseProductPanning();
                                const fullProduct = await getProductById(product.id);
                                if (fullProduct) { setSelectedProduct(fullProduct); setProductSidebarOpen(true); }
                              } catch (e) {
                                console.error('Error loading product:', e);
                              } finally {
                                setLoadingProductId(null);
                              }
                            }}
                          >
                            {loadingProductId === product.id ? 'Cargando...' : isUpdating ? 'Agregando...' : product.title}
                          </CardTitle>
                          <CardDescription className="line-clamp-1 text-xs">{stripHtml(product.description)}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2 pt-0 pb-3">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-lg font-bold text-primary">
                              {formatCurrency(inCart ? inCart.unit_price : product.fixed_sale_price)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {hasMoreProducts && (
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => loadMoreAisleProducts(aisleGroup.aisle.id, totalProducts)}
                      className="px-4"
                    >
                      Cargar mas ({totalProducts - visibleCount} restantes)
                    </Button>
                  </div>
                )}
              </div>
            );
            })}
          </>
        )}
      </div>
    )
  );

  const renderPostCarouselSkeleton = () => (
    <div className="space-y-6" aria-hidden="true">
      <div className="space-y-4">
        <div className="flex gap-2.5 overflow-x-hidden overflow-y-visible pt-1 pb-1 md:gap-4 md:pt-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={`post-carousel-store-skeleton-${idx}`}
              className="w-[196px] flex-shrink-0 px-1.5 py-1.5 sm:w-[220px] md:w-[312px] md:px-2 lg:w-[340px]"
            >
              <div className="flex h-[178px] flex-col gap-3 rounded-2xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/5 p-3 sm:h-[188px]">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
                    <div className="min-w-0 space-y-1.5">
                      <Skeleton className="h-4 w-24 rounded" />
                      <Skeleton className="h-3 w-14 rounded" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-3.5 w-5/6 rounded" />
                  <Skeleton className="h-3.5 w-2/3 rounded" />
                  <Skeleton className="h-3 w-1/2 rounded" />
                </div>

                <div className="mt-auto space-y-2">
                  <div className="flex flex-nowrap gap-1.5 overflow-hidden">
                    <Skeleton className="h-5 w-14 rounded-full shrink-0" />
                    <Skeleton className="h-5 w-18 rounded-full shrink-0" />
                    <Skeleton className="h-5 w-16 rounded-full shrink-0" />
                  </div>
                  <Skeleton className="h-8 w-full rounded-xl" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-3xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/5 p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,280px] lg:gap-6">
            <div className="space-y-3">
              <Skeleton className="h-6 w-36 rounded" />
              <Skeleton className="h-4 w-full max-w-2xl rounded" />
              <Skeleton className="h-4 w-2/3 rounded" />
            </div>
            <div className="space-y-2 rounded-2xl border border-primary/10 bg-background/80 p-3">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-8 w-full rounded-xl" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-20 rounded-xl" />
                <Skeleton className="h-7 w-24 rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-card via-card to-primary/10 p-4 shadow-sm sm:p-5 lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] lg:grid-cols-[auto,1fr,300px] gap-4 lg:gap-6 items-start">
          <Skeleton className="h-16 w-16 rounded-2xl" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-full max-w-2xl" />
            <Skeleton className="h-4 w-52" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-7 w-28 rounded-full" />
              <Skeleton className="h-7 w-36 rounded-full" />
              <Skeleton className="h-7 w-32 rounded-full" />
            </div>
          </div>
          <div className="rounded-2xl border border-primary/20 bg-background/80 p-3 sm:p-4 h-full flex flex-col gap-3">
            <Skeleton className="h-4 w-20" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-8 w-24 rounded-xl" />
              <Skeleton className="h-8 w-24 rounded-xl" />
              <Skeleton className="h-8 w-24 rounded-xl" />
            </div>
            <Skeleton className="h-8 w-full rounded-xl" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          {[1, 2, 3, 4, 5].map((chip) => (
            <Skeleton key={chip} className="h-8 w-24 rounded-full shrink-0" />
          ))}
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>

      <div className="space-y-6">{['post-skeleton-1', 'post-skeleton-2'].map((sectionKey) => renderCatalogSectionSkeleton(sectionKey))}</div>
    </div>
  );

  return (
    <StandardPage
      className="min-h-[100dvh] bg-transparent overflow-x-clip"
    >
      <div
        className="w-full min-h-[100dvh] overflow-x-clip"
        style={{
          paddingBottom: mobilePageBottomPadding,
        }}
      >
        {/* Features slider temporarily disabled */}

        {/* Tiendas cercanas */}
        <div ref={nearbySelectorRef}>
          <NearbyStoresSelector
            selectedStoreId={selectedStore.id}
            pinnedStore={DEFAULT_MINIBID_STORE}
            onRentStoreClick={handleRentStoreClick}
            rentStoreLoading={rentStoreLoading}
            onLoadingChange={setStoresCarouselLoading}
            onPromoCardSelectionChange={setPromoCardActive}
            onStoreSelect={(store, options) => {
              if (options?.triggerAutoScroll) {
                selectorDrivenAutoScrollRef.current = true;
              }
              const isManualReselect = options?.triggerAutoScroll && store.id === selectedStore.id;
              if (isManualReselect) {
                setStoreReselectNonce((value) => value + 1);
              }
              setInitialFeedStoreId((prev) => prev ?? store.id);
              setSelectedStore(store);
              setSelectedAisle(null);
            }}
            onViewModeChange={setStoreViewMode}
            renderStoreCatalog={() => renderProductCatalog()}
            renderStoreExpansion={() => (
              <div>
                <div className="bg-card p-4 sm:p-5 relative overflow-hidden rounded-2xl [clip-path:inset(0_round_1rem)] border border-primary/20">
                  <div className="absolute -right-10 -top-14 h-36 w-36 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
                  <div className="absolute -left-8 -bottom-12 h-28 w-28 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
                  <div className="relative z-10 grid grid-cols-1 sm:grid-cols-[auto,1fr] lg:grid-cols-[auto,1fr,300px] gap-4 lg:gap-6 items-start">
                    <Avatar className="h-16 w-16 rounded-2xl ring-2 ring-primary/20">
                      <AvatarImage src={effectiveStore.avatar_url || undefined} alt={effectiveStore.name} className="object-cover" />
                      <AvatarFallback className="rounded-2xl bg-primary/10 text-primary font-bold text-2xl">
                        {(effectiveStore.name || 'T').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-2 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight line-clamp-1">{effectiveStore.name}</h2>
                        <Link
                          to={`/${effectiveStore.slug || 'tienda'}`}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-primary/20 bg-background/80 text-muted-foreground hover:underline"
                        >
                          minibid.org/{effectiveStore.slug || 'tienda'}
                        </Link>
                        {isCurrentUserOnStoreTeam && (
                          <Link
                            to={storeDashboardPath}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-primary/20 bg-primary/10 text-primary hover:bg-primary/15"
                          >
                            <span className="font-bold">Entrar al tablero</span>
                            <motion.span
                              className="inline-block"
                              animate={{ x: [0, 4, -1, 0] }}
                              transition={{
                                duration: 1.35,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              }}
                            >
                              <ArrowRight className="w-3 h-3" />
                            </motion.span>
                          </Link>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground max-w-2xl whitespace-pre-wrap break-words">
                        {storeDescription || 'Explora productos organizados por secciones inteligentes de palabras clave.'}
                      </p>
                      <div className="text-xs">
                        {storeMetaLoading ? (
                          <Skeleton className="h-5 w-32" />
                        ) : (
                          (() => {
                          const open = isStoreOpenNow(effectiveStore);
                          const closing = getTodayClosingTime(effectiveStore);
                          if (open) {
                            return (
                              <div className="flex items-start gap-2 text-foreground">
                                <span className="mt-1 h-2 w-2 rounded-full bg-success shrink-0"></span>
                                <div className="flex flex-col leading-tight">
                                  <div className="font-medium">Abierto ahora</div>
                                  {closing ? <div className="text-muted-foreground">Cierra a {closing}</div> : null}
                                </div>
                              </div>
                            );
                          }
                          const next = getNextOpeningTime(effectiveStore);
                          const { dayIdx: todayIdx } = getStoreNowParts(getStoreWeekAndTimezone(effectiveStore).tz);
                          const dayNames = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
                          return (
                            <div className="flex items-start gap-2 text-foreground">
                              <span className="mt-1 h-2 w-2 rounded-full bg-error shrink-0"></span>
                              <div className="flex flex-col leading-tight">
                                <div className="font-medium">Cerrado</div>
                                {next ? (
                                  <div className="text-muted-foreground">
                                    Abre {next.dayIdx === todayIdx ? 'a' : `${dayNames[next.dayIdx]} a`} {next.time}
                                  </div>
                                ) : (
                                  <div className="text-muted-foreground">Horario no disponible</div>
                                )}
                              </div>
                            </div>
                          );
                          })()
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground min-w-0">
                        {deliveryAvailable && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2 py-1 text-foreground w-fit">
                            Envío disponible
                          </span>
                        )}
                        {pickupAvailable && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2 py-1 text-foreground w-fit">
                            Retiro personal en tienda
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 w-fit">
                          <MapPin className="h-3.5 w-3.5 text-primary" />
                          {selectedStore.distance !== undefined ? `${selectedStore.distance < 1 ? Math.round(selectedStore.distance * 1000) + ' m' : selectedStore.distance.toFixed(1) + ' km'}` : ((effectiveStore as any).location_display_name || 'Zona cercana')}
                        </span>
                        {storeAddresses.length > 0 && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 hover:text-foreground w-full sm:w-auto sm:max-w-full min-w-0 text-xs leading-none"
                            onClick={() => {
                              setDirectionsSidebarOpen(true);
                            }}
                          >
                            <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                            <span className="truncate text-xs leading-none">{storeAddresses[0]?.location?.displayName || 'Ver direccion'}</span>
                          </button>
                        )}
                        {storeTeam.length >= 2 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 w-fit">
                            <Users className="h-3.5 w-3.5 text-primary" />
                            {`${storeTeam.length} en el equipo`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-full sm:col-span-2 lg:col-span-1 lg:min-w-[260px]">
                      <div className="rounded-2xl bg-background/80 p-3 sm:p-4 h-full flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Equipo</div>
                        </div>
                        {storeMetaLoading ? (
                          <div className="text-xs text-muted-foreground">Cargando equipo...</div>
                        ) : (
                          <div className="flex flex-col gap-2 h-full">
                            <div className="flex flex-wrap gap-1.5">
                              {visibleTeam.map((member) => (
                                <button
                                  key={member.user_id}
                                  type="button"
                                  onClick={() => handleOpenTeamMemberProfile(member)}
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-primary/15 bg-primary/5 px-1.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                                  aria-label={`Ver perfil de ${member.display_name || 'miembro'}`}
                                >
                                  <Avatar className="h-6 w-6 border border-primary/20">
                                    <AvatarImage src={member.avatar_url || undefined} alt={member.display_name} />
                                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                      {(member.display_name || 'M').charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="min-w-0 max-w-[88px] text-[11px] font-medium text-foreground">
                                    <span className="inline-flex items-center gap-1">
                                      <span className="truncate max-w-[66px]">
                                        {(() => {
                                          const parts = (member.display_name || 'Miembro').trim().split(/\s+/).filter(Boolean);
                                          const firstName = parts[0] || 'Miembro';
                                          const lastInitial = parts.length > 1 ? ` ${parts[parts.length - 1].charAt(0).toUpperCase()}.` : '';
                                          return `${firstName}${lastInitial}`;
                                        })()}
                                      </span>
                                      <VerifiedBadge verified={member.verification_status === 'verified'} size={12} className="shrink-0" />
                                    </span>
                                  </span>
                                </button>
                              ))}
                              {visibleTeam.length === 0 && (
                                <span className="text-xs text-muted-foreground text-center py-2">Sin miembros visibles</span>
                              )}
                            </div>
                            <div className="pt-1 border-t border-primary/10 mt-auto">
                              <button
                                type="button"
                                onClick={handleStartStoreGroupChat}
                                disabled={startingStoreChat || (!!session?.user?.id && (storeChatMembersLoading || storeChatMemberIds.length === 0))}
                                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl border border-primary/20 bg-background hover:bg-primary/10 hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium text-primary"
                              >
                                <MessageSquareText className="w-3.5 h-3.5" />
                                {startingStoreChat ? 'Abriendo...' : storeChatMembersLoading ? 'Preparando chat...' : 'Chatear con la tienda'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          />
        </div>

        {storesCarouselLoading ? (
          renderPostCarouselSkeleton()
        ) : promoCardActive ? null : (
        <>
        {storeViewMode !== 'directory' && (
        <div ref={storeHeaderRef} className="mb-6">
          <div className="relative rounded-3xl border border-primary/20 bg-card p-4 sm:p-5 lg:p-6 overflow-hidden [clip-path:inset(0_round_1.5rem)] shadow-sm">
            <div className="absolute -right-10 -top-14 h-36 w-36 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
            <div className="absolute -left-8 -bottom-12 h-28 w-28 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 sm:grid-cols-[auto,1fr] lg:grid-cols-[auto,1fr,300px] gap-4 lg:gap-6 items-start">
              <Avatar className="h-16 w-16 rounded-2xl ring-2 ring-primary/20">
                <AvatarImage src={effectiveStore.avatar_url || undefined} alt={effectiveStore.name} className="object-cover" />
                <AvatarFallback className="rounded-2xl bg-primary/10 text-primary font-bold text-2xl">
                  {(effectiveStore.name || 'T').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="space-y-2 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight line-clamp-1">{effectiveStore.name}</h2>
                  <Link
                    to={`/${effectiveStore.slug || 'tienda'}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-primary/20 bg-background/80 text-muted-foreground hover:underline"
                  >
                    minibid.org/{effectiveStore.slug || 'tienda'}
                  </Link>
                  {isCurrentUserOnStoreTeam && (
                    <Link
                      to={storeDashboardPath}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-primary/20 bg-primary/10 text-primary hover:bg-primary/15"
                    >
                      <span className="font-bold">Entrar al tablero</span>
                      <motion.span
                        className="inline-block"
                        animate={{ x: [0, 4, -1, 0] }}
                        transition={{
                          duration: 1.35,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <ArrowRight className="w-3 h-3" />
                      </motion.span>
                    </Link>
                  )}
                </div>

                <p className="text-sm text-muted-foreground max-w-2xl whitespace-pre-wrap break-words">
                  {storeDescription || 'Explora productos organizados por secciones inteligentes de palabras clave.'}
                </p>

                <div className="text-xs">
                  {storeMetaLoading ? (
                    <Skeleton className="h-5 w-32" />
                  ) : (
                    (() => {
                    const open = isStoreOpenNow(effectiveStore);
                    const closing = getTodayClosingTime(effectiveStore);
                    if (open) {
                      return (
                        <div className="flex items-start gap-2 text-foreground">
                          <span className="mt-1 h-2 w-2 rounded-full bg-success shrink-0"></span>
                          <div className="flex flex-col leading-tight">
                            <div className="font-medium">Abierto ahora</div>
                            {closing ? <div className="text-muted-foreground">Cierra a {closing}</div> : null}
                          </div>
                        </div>
                      );
                    }

                    const next = getNextOpeningTime(effectiveStore);
                    const { dayIdx: todayIdx } = getStoreNowParts(getStoreWeekAndTimezone(effectiveStore).tz);
                    const dayNames = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

                    return (
                      <div className="flex items-start gap-2 text-foreground">
                        <span className="mt-1 h-2 w-2 rounded-full bg-error shrink-0"></span>
                        <div className="flex flex-col leading-tight">
                          <div className="font-medium">Cerrado</div>
                          {next ? (
                            <div className="text-muted-foreground">
                              Abre {next.dayIdx === todayIdx ? 'a' : `${dayNames[next.dayIdx]} a`} {next.time}
                            </div>
                          ) : (
                            <div className="text-muted-foreground">Horario no disponible</div>
                          )}
                        </div>
                      </div>
                    );
                    })()
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground min-w-0">
                  {deliveryAvailable && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2 py-1 text-foreground w-fit">
                      Envío disponible
                    </span>
                  )}
                  {pickupAvailable && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2 py-1 text-foreground w-fit">
                      Retiro personal en tienda
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 w-fit">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                    {selectedStore.distance !== undefined ? `${selectedStore.distance < 1 ? Math.round(selectedStore.distance * 1000) + ' m' : selectedStore.distance.toFixed(1) + ' km'}` : ((effectiveStore as any).location_display_name || 'Zona cercana')}
                  </span>
                  {storeAddresses.length > 0 && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 hover:text-foreground w-full sm:w-auto sm:max-w-full min-w-0 text-xs leading-none"
                      onClick={() => {
                        setDirectionsSidebarOpen(true);
                      }}
                    >
                      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="truncate text-xs leading-none">{storeAddresses[0]?.location?.displayName || 'Ver direccion'}</span>
                    </button>
                  )}
                  {storeTeam.length >= 2 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/80 px-2 py-1 w-fit">
                      <Users className="h-3.5 w-3.5 text-primary" />
                      {`${storeTeam.length} en el equipo`}
                    </span>
                  )}
                </div>
              </div>

              <div className="w-full sm:col-span-2 lg:col-span-1 lg:min-w-[260px]">
                <div className="rounded-2xl bg-background/80 p-3 sm:p-4 h-full flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Equipo</div>
                  </div>
                  {storeMetaLoading ? (
                    <div className="text-xs text-muted-foreground">Cargando equipo...</div>
                  ) : (
                    <div className="flex flex-col gap-2 h-full">
                      <div className="flex flex-wrap gap-1.5">
                        {visibleTeam.map((member) => (
                          <button
                            key={member.user_id}
                            type="button"
                            onClick={() => handleOpenTeamMemberProfile(member)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-primary/15 bg-primary/5 px-1.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                            aria-label={`Ver perfil de ${member.display_name || 'miembro'}`}
                          >
                            <Avatar className="h-6 w-6 border border-primary/20">
                              <AvatarImage src={member.avatar_url || undefined} alt={member.display_name} />
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                {(member.display_name || 'M').charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="min-w-0 max-w-[88px] text-[11px] font-medium text-foreground">
                              <span className="inline-flex items-center gap-1">
                                <span className="truncate max-w-[66px]">
                                  {(() => {
                                    const parts = (member.display_name || 'Miembro').trim().split(/\s+/).filter(Boolean);
                                    const firstName = parts[0] || 'Miembro';
                                    const lastInitial = parts.length > 1 ? ` ${parts[parts.length - 1].charAt(0).toUpperCase()}.` : '';
                                    return `${firstName}${lastInitial}`;
                                  })()}
                                </span>
                                <VerifiedBadge verified={member.verification_status === 'verified'} size={12} className="shrink-0" />
                              </span>
                            </span>
                          </button>
                        ))}
                        {visibleTeam.length === 0 && (
                          <span className="text-xs text-muted-foreground text-center py-2">Sin miembros visibles</span>
                        )}
                      </div>
                      <div className="pt-1 border-t border-primary/10 mt-auto">
                        <button
                          type="button"
                          onClick={handleStartStoreGroupChat}
                          disabled={startingStoreChat || (!!session?.user?.id && (storeChatMembersLoading || storeChatMemberIds.length === 0))}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl border border-primary/20 bg-background hover:bg-primary/10 hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium text-primary"
                        >
                          <MessageSquareText className="w-3.5 h-3.5" />
                          {startingStoreChat ? 'Abriendo...' : storeChatMembersLoading ? 'Preparando chat...' : 'Chatear con la tienda'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Collaborative Activity Card */}
        {(() => {
          const { isShared: sharedActive } = getActiveSharedMetaForStore(selectedStore.id);

          if (!sharedActive) return null;

          const getActivityActorName = (actorUserId: string | null | undefined) => {
            if (!actorUserId) return 'Alguien';
            if (actorUserId === session?.user?.id) return 'Vos';
            return sharedActivityActorNameByUserId[actorUserId] || 'Alguien';
          };

          const getActivityMessage = (log: SharedCartActivityLog) => {
            const actorName = getActivityActorName(log.actor_user_id);
            const isYou = actorName === 'Vos';
            const productTitle = log.product_title || 'producto';
            const quantityAfter = Number(log.quantity_after || 0);
            const quantityBefore = Number(log.quantity_before || 0);

            if (log.action === 'added') {
              return isYou
                ? `Agregaste ${quantityAfter}x ${productTitle}`
                : `${actorName} agregó ${quantityAfter}x ${productTitle}`;
            }

            if (log.action === 'removed') {
              return isYou
                ? `Quitaste ${productTitle}`
                : `${actorName} quitó ${productTitle}`;
            }

            if (log.action === 'cleared') {
              return isYou
                ? 'Vaciaste el canasto'
                : `${actorName} vació el canasto`;
            }

            return isYou
              ? `Actualizaste ${productTitle} (${quantityBefore} -> ${quantityAfter})`
              : `${actorName} actualizó ${productTitle} (${quantityBefore} -> ${quantityAfter})`;
          };

          return (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-6">
              <p className="text-sm font-semibold text-primary mb-3">🤝 Actividad colaborativa</p>
              <div className="max-h-48 overflow-y-auto pr-2 space-y-2">
                {sharedActivityLogs.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin actividad reciente.</p>
                ) : (
                  sharedActivityLogs.map((log) => {
                    let hourLabel = '--:--';
                    try {
                      hourLabel = format(parseISO(log.created_at), 'HH:mm');
                    } catch {
                      hourLabel = '--:--';
                    }

                    return (
                      <p key={log.id} className="text-xs text-muted-foreground break-words">
                        <span className="text-primary/80 font-semibold">{hourLabel}</span>
                        {' · '}
                        {getActivityMessage(log)}
                      </p>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}

        {/* Loading State */}
        <div ref={catalogSectionRef}>
          {storeViewMode !== 'directory' && renderProductCatalog()}
        </div>
        </>
        )}
      </div>
      
      <AnimatePresence>
        {teamProfileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-primary/20 lg:bg-black/40 z-[9999]"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setTeamProfileSidebarOpen(false);
              }}
              onTouchStart={(e) => {
                if (e.target === e.currentTarget) setTeamProfileSidebarOpen(false);
              }}
            />

            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.2 }}
              className={`fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[10000] flex flex-col overflow-x-hidden ${productSidebarOpen && isDesktop ? 'hidden' : ''}`}
              style={{
                top: isDesktop ? `${headerHeight}px` : 0,
                bottom: !isDesktop ? 0 : undefined,
                height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
              }}
            >
              {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

              <div className="flex items-center justify-between gap-3 p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
                <h3 className="text-base font-semibold truncate">
                  <span className="inline-flex items-center gap-2">
                    <span className="truncate">{selectedTeamProfile?.display_name || selectedTeamMember?.display_name || 'Perfil'}</span>
                    <VerifiedBadge verified={(selectedTeamProfile?.verification_status || selectedTeamMember?.verification_status) === 'verified'} size={16} className="shrink-0" />
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => setTeamProfileSidebarOpen(false)}
                  aria-label="Cerrar"
                  className="p-2 rounded hover:bg-primary-foreground/10 text-primary-foreground transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
                <div className="relative isolate overflow-hidden rounded-[28px] border border-primary/15 bg-gradient-to-br from-card via-card to-primary/10 shadow-sm">
                  <div className="pointer-events-none absolute -right-10 -top-10 z-0 h-28 w-28 rounded-full bg-primary/15 blur-3xl" />
                  <div className="pointer-events-none absolute left-0 top-0 z-0 h-20 w-full bg-gradient-to-r from-primary/90 via-primary to-primary/70" />
                  <div className="relative z-20 px-4 pb-4 pt-6">
                    <div className="relative z-30 flex items-start gap-3">
                      <Avatar className="h-20 w-20 shrink-0 border-4 border-background shadow-lg ring-1 ring-primary/15">
                        <AvatarImage
                          src={(selectedTeamProfile?.avatar_url || selectedTeamMember?.avatar_url) || undefined}
                          alt={selectedTeamProfile?.display_name || selectedTeamMember?.display_name || 'Miembro'}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold text-xl">
                          {((selectedTeamProfile?.display_name || selectedTeamMember?.display_name || 'M').charAt(0)).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1 pt-3">
                        <div className="relative z-30 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded-full border border-primary/15 bg-background/85 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-sm backdrop-blur">
                            {getStoreRoleLabel(selectedTeamMember?.role || 'staff')}
                          </span>
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground shadow-sm">
                            Equipo {effectiveStore.name || 'Minibid'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="relative z-20 mt-4 rounded-2xl border border-primary/10 bg-background/85 p-4 backdrop-blur-sm">
                      <div className="space-y-3">
                        <div className="min-w-0">
                          <p className="text-lg font-extrabold tracking-tight text-foreground">
                            <span className="inline-flex max-w-full items-center gap-1.5">
                              <span className="truncate">{selectedTeamProfile?.display_name || selectedTeamMember?.display_name || 'Miembro'}</span>
                              <VerifiedBadge verified={(selectedTeamProfile?.verification_status || selectedTeamMember?.verification_status) === 'verified'} size={16} className="shrink-0" />
                            </span>
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground truncate">
                            @{selectedTeamProfile?.username || selectedTeamMember?.username || 'usuario'}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                          <div className="rounded-xl border border-primary/10 bg-primary/5 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Perfil</p>
                            <p className="mt-1 text-sm font-medium text-foreground truncate">
                              minibid.org/{selectedTeamProfile?.username || selectedTeamMember?.username || 'usuario'}
                            </p>
                          </div>

                          {selectedTeamProfile?.relevant_location_display_name && (
                            <div className="rounded-xl border border-primary/10 bg-background px-3 py-2">
                              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Ubicacion</p>
                              <p className="mt-1 inline-flex max-w-full items-center gap-1.5 text-sm text-foreground">
                                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                                <span className="truncate">{selectedTeamProfile.relevant_location_display_name}</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedTeamProfileLoading && (
                  <div className="text-sm text-muted-foreground">Cargando perfil...</div>
                )}

                {selectedTeamProfile?.id && (selectedTeamProfile?.username || selectedTeamMember?.username) && (
                  <ChatButton
                    userId={selectedTeamProfile.id}
                    username={selectedTeamProfile.username || selectedTeamMember?.username || 'usuario'}
                    avatarUrl={selectedTeamProfile.avatar_url || selectedTeamMember?.avatar_url}
                    variant="outline"
                    className="w-full"
                    disabled={session?.user?.id === selectedTeamProfile.id}
                  >
                    Chatear
                  </ChatButton>
                )}

                <div className="pt-2 border-t border-primary/10">
                  <h4 className="text-sm font-semibold mb-3">Actividad reciente</h4>
                  {selectedTeamNotesLoading ? (
                    <div className="text-sm text-muted-foreground">Cargando notas...</div>
                  ) : selectedTeamNotes.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Este usuario no tiene actividad reciente visible.</div>
                  ) : (
                    <div className="space-y-3">
                      {visibleTeamActivity.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          currentUserId={session?.user?.id}
                          session={session}
                          onDelete={() => {}}
                        />
                      ))}
                      {selectedTeamNotesVisibleCount < selectedTeamNotes.length && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setSelectedTeamNotesVisibleCount((count) => count + TEAM_ACTIVITY_PAGE_SIZE)}
                        >
                          Cargar mas
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <StoreGetDirectionsSidebar
        storeId={effectiveStore.id}
        isOpen={directionsSidebarOpen}
        onClose={() => setDirectionsSidebarOpen(false)}
        addresses={storeAddresses.map((address) => ({
          id: address.id,
          label: address.label,
          location: {
            displayName: address.location?.displayName || address.label || 'Direccion',
            latitude: address.location?.latitude,
            longitude: address.location?.longitude,
          },
          is_primary: address.is_primary,
          floor: address.floor,
          apartment: address.apartment,
          notes: address.notes,
        }))}
      />
    </StandardPage>
  );
}
